/**
 * Translation.
 *
 * Keyed by the English source string rather than by an invented id. Two reasons
 * that matter more than the tidiness of `t('browse.title')`:
 *
 *  * A missing translation falls back to a real sentence instead of a key leaking
 *    onto the screen. `browse.title` in front of a user is worse than English.
 *  * The code stays readable — `t('Post a card')` says what it renders, so nobody
 *    has to open a dictionary to review a screen.
 *
 * The cost is that changing English copy orphans its translation. That is a real
 * cost, and it is checked: `missingTranslations()` lists every string with no
 * Spanish, and a test fails when the list grows.
 *
 * **Spanish is the fallback, not English** — the product launches in Spain. So a
 * visitor with no stated preference gets Spanish unless their browser asks for
 * English.
 */

const ES = {
  // --- chrome, navigation ---------------------------------------------------
  "Browse": "Explorar",
  "Trades": "Intercambios",
  "Inbox": "Mensajes",
  "Post": "Publicar",
  "Post a card": "Publicar una carta",
  "Sign in": "Iniciar sesión",
  "Your profile": "Tu perfil",
  "Set your location": "Marca dónde estás",
  "Cardboardmarket home": "Inicio de Cardboardmarket",
  "Where are you trading from?": "¿Desde dónde cambias?",
  "Save": "Guardar",
  "Cancel": "Cancelar",
  "Back": "Atrás",
  // The corner button on every dialog in the product. It has no visible text —
  // it is the accessible name and the tooltip — which is exactly why it stayed
  // English through four i18n checks and a visual suite: nothing photographs it
  // and nothing reads it aloud except a screen reader, in Spanish, saying "Close".
  "Close": "Cerrar",
  "Radius": "Radio",
  "Language": "Idioma",

  // --- the places you trade from (migration 052) ----------------------------
  // "Mi punto" is also the server's default label and migration 052's backfill.
  // The three have to say the same words: a trader who never types a name sees
  // one of them on the chip and the other two in their export.
  "My point": "Mi punto",
  "Your locations": "Tus ubicaciones",
  "Name for this location": "Nombre de esta ubicación",
  "Add a location": "Añadir una ubicación",
  "Save location": "Guardar ubicación",
  "Location saved.": "Ubicación guardada.",
  "Location deleted.": "Ubicación eliminada.",
  "Now trading from {label}.": "Ahora cambias desde {label}.",
  "Rename or move {label}": "Cambiar el nombre o mover {label}",
  "Delete {label}": "Eliminar {label}",
  "You can save up to {n} locations. Delete one to add another.": "Puedes guardar hasta {n} ubicaciones. Elimina una para añadir otra.",
  "Only you ever see this.": "Solo lo ves tú.",
  "Saved as {label}. Distances are measured from the pin above.": "Guardado como {label}. Las distancias se miden desde el pin de arriba.",
  "Only you ever see this. You can save more later.": "Solo lo ves tú. Puedes guardar más luego.",

  // --- browse ---------------------------------------------------------------
  "Cards near you": "Cartas cerca de ti",
  "Card, set, or @trader": "Carta, colección o @usuario",
  "Everything": "Todo",
  "For sale": "En venta",
  "For trade": "Para cambiar",
  // The browse tab bar's own name, read by a screen reader before its tabs.
  "Listings": "Anuncios",
  "Start typing a card name": "Empieza a escribir el nombre de una carta",
  "Newest": "Más recientes",
  "Closest": "Más cerca",
  "Price: low to high": "Precio: de menor a mayor",
  "Price: high to low": "Precio: de mayor a menor",
  // The shop's Stock table orders by these as well; browse does not.
  "Name: A to Z": "Nombre: de la A a la Z",
  "Name: Z to A": "Nombre: de la Z a la A",
  "Condition: best first": "Estado: mejor primero",
  "Condition: worst first": "Estado: peor primero",
  "Oldest first": "Más antiguas",
  "Distance": "Distancia",
  // The filter rail with no point set: the way out, and why the slot is empty.
  "Set where you are": "Marca dónde estás",
  "Until then, distance filters nothing.": "Hasta entonces la distancia no filtra nada.",
  "Game": "Juego",
  "Filters": "Filtros",
  "From your listings": "De tus anuncios",
  // A want with no matches yet: the status of the entry, in lower case beside it.
  "watching": "a la espera",
  "Loading the pictures\u2026": "Cargando las fotos\u2026",
  "Games you play": "Juegos a los que juegas",

  // Being reachable outside the app. Neutral, professional Spanish — the primary
  // locale, not a translation of the English.
  "How people can reach you": "Cómo pueden contactarte",
  "Optional. Saving a contact does not show it to anybody — that is the switch beside it, and you can turn it off at any time.":
    "Opcional. Guardar un contacto no se lo muestra a nadie: eso lo decide el interruptor de al lado, y puedes desactivarlo cuando quieras.",
  "Telegram username": "Usuario de Telegram",
  // "Dónde tienes el stock de verdad" — "de verdad" rather than "principal",
  // because the question is which copy is authoritative, not which is bigger.
  "Where your stock really lives": "Dónde tienes el stock de verdad",
  // "Empezar a seguir" rather than "Seguir": the button sits under a form that
  // defines HOW, so it commits to a setting rather than performing an action.
  "Follow the market on {count} listings": "Seguir el mercado en {count} anuncios",
  // Says the three things a shop needs before handing prices over: when it
  // happens, what is skipped, and that an agreed trade is safe.
  "Every night we read the price on Cardmarket and set yours from it. A card we cannot match keeps the price you typed, and a trade already proposed keeps the price it was agreed at.":
    "Cada noche leemos el precio en Cardmarket y ponemos el tuyo a partir de él. Una carta que no podamos emparejar se queda con el precio que escribiste, y un cambio ya propuesto mantiene el precio al que se acordó.",
  "Start following": "Empezar a seguir",
  "Stop following": "Dejar de seguir",
  // CARDTRADER: conectar la cuenta de una tienda.
  //
  // "Token" se queda en inglés porque es la palabra que usa CardTrader en su
  // propia pantalla de ajustes — traducirla a "credencial" obligaría a la
  // tienda a traducir de vuelta mientras busca el botón.
  // "Sincronización" nombra la FUNCIÓN, no la credencial: una tienda que lee
  // "Tu cuenta de CardTrader" tiene que deducir qué haríamos con ella.
  "Synchronisation": "Sincronización",
  // EL WEBHOOK: opcional de verdad, y la copia lo dice. El barrido de cinco
  // minutos ya ve todo lo que vería esto, ventas incluidas; esto solo adelanta
  // el momento. Una tienda que no lo active no se queda a medias.
  "Address copied.": "Dirección copiada.",
  "Last checked {when}": "Última comprobación: {when}",
  "Instant notice of a sale": "Aviso instantáneo de una venta",
  "How to set this up in CardTrader, step by step":
    "Cómo configurarlo en CardTrader, paso a paso",
  // Las etiquetas de CardTrader van en inglés a propósito: su interfaz lo está,
  // y un botón traducido es un botón que la tienda no encuentra.
  // LA DIRECCIÓN, DICHA EN LAS DOS MITADES. La frase anterior decía "una venta
  // deja de aparecer aquí", que se leía al revés — como si vender aquí quitara
  // la carta de CardTrader. Es al contrario, y tiene que serlo: un webhook solo
  // avisa de que algo pasó, nada de lo que manda CardTrader nos deja cambiar su
  // lado.
  "Optional. Without it we check your CardTrader stock every five minutes, which already catches everything — sales included. With it, a card you sell on CardTrader stops showing here within seconds.":
    "Opcional. Sin esto comprobamos tu stock de CardTrader cada cinco minutos, que ya lo pilla todo — ventas incluidas. Con esto, una carta que vendas en CardTrader deja de aparecer aquí en segundos.",
  "Show me how": "Enséñame cómo",
  "Your address": "Tu dirección",
  // "Guárdala en secreto" dice qué HACER; "solo tuya" solo describía. Y la
  // coletilla de por qué parece un galimatías se fue: contestaba una pregunta
  // que nadie tenía, en mitad de un aviso.
  "Keep it a secret. Anyone with it could tell us your stock changed.":
    "Guárdala en secreto. Cualquiera que la tuviera podría decirnos que tu stock ha cambiado.",
  // Por qué parece un galimatías: no es decoración, es que vale como una llave.
  // La única prueba que podemos dar: no podemos leer sus ajustes, así que
  // decimos que algo llegó y cuándo, en vez de afirmar que está "conectado".
  "Working — the last notice arrived {when}.": "Funcionando — el último aviso llegó {when}.",
  "Nothing has arrived yet. It only fires when something sells, so this may simply mean nothing has.":
    "Todavía no ha llegado nada. Solo salta cuando se vende algo, así que puede ser simplemente que no se haya vendido nada.",
  // Decimos que NO podemos mirar, en vez de fingir que hemos mirado.
  "If you sell the same cards somewhere else, we can keep your stock here in step with it — so a card you sold there stops showing here.":
    "Si vendes las mismas cartas en otro sitio, podemos mantener tu stock de aquí al día con él — para que una carta que vendiste allí deje de aparecer aquí.",
  // Sin promesas: Cardmarket no tiene una API que permita esto, así que "por
  // ahora" dice la verdad y no insinúa una hoja de ruta en una pantalla de ajustes.
  "Right now we can do this with CardTrader.": "Por ahora podemos hacerlo con CardTrader.",
  "Paste the API token from your profile settings there, and we take it from there.":
    "Pega el token de la API de los ajustes de tu perfil de allí y nosotros nos encargamos.",
  "Connect": "Conectar",
  "That did not save.": "No se ha guardado.",
  "CardTrader connected.": "CardTrader conectado.",
  "CardTrader disconnected.": "CardTrader desconectado.",
  "Connected": "Conectada",
  "Disconnect": "Desconectar",
  "Token {tail}": "Token {tail}",
  "CardTrader user {id}": "Usuario {id} de CardTrader",
  "CardTrader API token": "Token de la API de CardTrader",
  "CardTrader profile → settings → API. We never show it back to you.":
    "Perfil de CardTrader → ajustes → API. No te lo volvemos a enseñar nunca.",
  "Paste the API token from your CardTrader profile settings and we will keep your stock here in step with it.":
    "Pega el token de la API de los ajustes de tu perfil de CardTrader y mantenemos tu stock de aquí al día con él.",
  // La frase que la tienda tiene que leer ANTES de pegar nada.
  "CardTrader only issues one kind of token, and it can change your listings there as well as read them. We encrypt it, we only read with it unless you switch the setting below on, and you can disconnect whenever you like.":
    "CardTrader solo emite un tipo de token, y sirve tanto para leer tus anuncios de allí como para cambiarlos. Nosotros lo ciframos, solo leemos con él salvo que actives la opción de abajo, y puedes desconectarlo cuando quieras.",
  "Connecting a CardTrader account is switched off right now. Nothing you paste here could be stored safely, so we are not asking for it.":
    "Ahora mismo no se puede conectar una cuenta de CardTrader. Nada de lo que pegaras aquí podría guardarse de forma segura, así que no te lo pedimos.",
  "CardTrader did not accept that token. Check you copied all of it from their settings page.":
    "CardTrader no ha aceptado ese token. Comprueba que lo copiaste entero de su página de ajustes.",
  "We could not reach CardTrader just now. Your token was not stored — try again in a minute.":
    "No hemos podido llegar a CardTrader ahora mismo. Tu token no se ha guardado; inténtalo dentro de un minuto.",
  // CardTrader refusing US for a while (Cloudflare's challenge, earned by our
  // own volume), not the shop's token. It says wait and never "check your
  // token": regenerating one that was fine is the worst advice there is, and
  // it is what we gave until 2026-10-13.
  "CardTrader is not answering right now. Your token was not stored — try again in a few minutes.":
    "CardTrader no responde ahora mismo. Tu token no se ha guardado; inténtalo dentro de unos minutos.",
  // The same two refusals, met by the nightly stock check rather than by the
  // shop connecting: the back office reads the code the sweep stored.
  "CardTrader no longer accepts the token this shop connected with. Connect it again with a fresh one.":
    "CardTrader ya no acepta el token con el que se conectó esta tienda. Conéctala de nuevo con uno nuevo.",
  "CardTrader was not answering when we last checked. The next check will try again.":
    "CardTrader no respondía en la última comprobación. La siguiente lo volverá a intentar.",
  // Any other failure of that check, which is ours: the sweep stores only the
  // code `sweep_failed` for it, never what went wrong.
  "The last check of your CardTrader stock failed at our end. The next check will try again.":
    "La última comprobación de tu stock en CardTrader ha fallado por nuestra parte. La siguiente lo volverá a intentar.",
  // The connection warning at the top of the back office (ADR 0011): raised
  // at once for a refused token, after an hour for an outage, cleared by the
  // next success. Two messages, because one asks the shop to act and the
  // other tells it not to.
  "CardTrader no longer accepts your shop's token.": "CardTrader ya no acepta el token de tu tienda.",
  "CardTrader has not answered for over an hour.": "CardTrader lleva más de una hora sin responder.",
  "Until you connect a fresh token, your stock here does not follow CardTrader's, and copies you promise in trades here are not taken off there. Generate a new one in your CardTrader profile (settings → API) and paste it on your store page.":
    "Hasta que conectes un token nuevo, tu stock de aquí no sigue al de CardTrader y las copias que prometas en cambios de aquí no se descuentan allí. Genera uno en tu perfil de CardTrader (ajustes → API) y pégalo en la ficha de tu tienda.",
  "While it lasts, your stock here is not updated and anything to take off CardTrader waits. There is nothing to do: we keep trying, and this goes by itself as soon as CardTrader answers.":
    "Mientras dure, tu stock de aquí no se actualiza y lo que haya que descontar en CardTrader espera. No tienes que hacer nada: seguimos intentándolo y este aviso desaparece solo en cuanto CardTrader responda.",
  "Failing since {when}": "Falla desde {when}",
  "Connect it again": "Conectar de nuevo",
  // Today's figure: writes to CardTrader that did not land. "Fallidas" over
  // "con error", which would read as ours.
  "CardTrader writes that failed": "Escrituras en CardTrader fallidas",

  // --- write-back: the switch and its three confirmations (ADR 0011) --------
  // "Descontar" for taking copies off and "devolver" for putting them back,
  // the words the connection warning above already uses. The switch's name is
  // the one the guides quote.
  "Update my CardTrader stock": "Actualizar mi stock de CardTrader",
  "When you accept a trade here, we take those copies off your CardTrader stock, and put them back if the trade is cancelled. Only the quantity, never a price.":
    "Cuando aceptas un cambio aquí, descontamos esas copias de tu stock de CardTrader y las devolvemos si el cambio se cancela. Solo la cantidad, nunca un precio.",
  "How updating your CardTrader stock works": "Cómo funciona actualizar tu stock de CardTrader",
  "Update your CardTrader stock?": "¿Actualizar tu stock de CardTrader?",
  "Stop updating your CardTrader stock?": "¿Dejar de actualizar tu stock de CardTrader?",
  "Disconnect CardTrader?": "¿Desconectar CardTrader?",
  "From now on, accepting a trade here takes those copies off your CardTrader stock, and cancelling it puts them back. We only ever change the quantity — never a price, and never a card you posted here by hand.":
    "A partir de ahora, aceptar un cambio aquí descuenta esas copias de tu stock de CardTrader, y cancelarlo las devuelve. Solo cambiamos la cantidad: nunca un precio, y nunca una carta que publicaste aquí a mano.",
  "Accepting or cancelling a trade here will no longer change your CardTrader stock. From then on, keeping it right is up to you — for trades already open too.":
    "Aceptar o cancelar un cambio aquí ya no cambiará tu stock de CardTrader. Desde ese momento, tenerlo al día te toca a ti, también en los cambios que ya están abiertos.",
  "Your stock here stops following CardTrader, and we delete your token.":
    "Tu stock de aquí deja de seguir al de CardTrader y borramos tu token.",
  "Switch on": "Activar",
  "Switch off": "Desactivar",
  "Done. Accepting a trade here now takes those copies off CardTrader.":
    "Hecho. Ahora aceptar un cambio aquí descuenta esas copias en CardTrader.",
  "Done. Accepting a trade here no longer changes CardTrader.":
    "Hecho. Aceptar un cambio aquí ya no toca CardTrader.",
  // The counts, one key per shape of number.
  "No open trade has copies to take off right now.": "Ahora mismo ningún cambio abierto tiene copias que descontar.",
  "1 copy from an open trade comes off CardTrader now.": "Ahora se descuenta en CardTrader 1 copia de un cambio abierto.",
  "{copies} copies from an open trade come off CardTrader now.": "Ahora se descuentan en CardTrader {copies} copias de un cambio abierto.",
  "{copies} copies from {trades} open trades come off CardTrader now.": "Ahora se descuentan en CardTrader {copies} copias de {trades} cambios abiertos.",
  "No copies are off CardTrader because of trades here right now.": "Ahora mismo no hay copias descontadas en CardTrader por cambios de aquí.",
  "1 copy is off CardTrader right now because of a trade here.": "Ahora mismo hay 1 copia descontada en CardTrader por un cambio de aquí.",
  "{copies} copies are off CardTrader right now because of a trade here.": "Ahora mismo hay {copies} copias descontadas en CardTrader por un cambio de aquí.",
  "{copies} copies are off CardTrader right now because of {trades} trades here.": "Ahora mismo hay {copies} copias descontadas en CardTrader por {trades} cambios de aquí.",
  "1 more copy was sent to CardTrader without an answer. We are still checking it.":
    "Además, 1 copia se envió a CardTrader sin respuesta. Todavía la estamos comprobando.",
  "{copies} more copies were sent to CardTrader without an answer. We are still checking them.":
    "Además, {copies} copias se enviaron a CardTrader sin respuesta. Todavía las estamos comprobando.",
  "1 copy still waiting to come off stays on sale there.": "1 copia que esperaba a descontarse sigue a la venta allí.",
  "{copies} copies still waiting to come off stay on sale there.": "{copies} copias que esperaban a descontarse siguen a la venta allí.",
  "If any of those trades falls through, or already has, putting the copies back on CardTrader is yours to do by hand: we will not be able to.":
    "Si alguno de esos cambios no sale, o ya no salió, volver a poner las copias en CardTrader te toca a ti, a mano: nosotros ya no podremos.",
  "If any of those trades falls through, or already has, putting the copies back on CardTrader is yours to do by hand: we will not do it, even if you switch this on again.":
    "Si alguno de esos cambios no sale, o ya no salió, volver a poner las copias en CardTrader te toca a ti, a mano: nosotros no lo haremos, aunque vuelvas a activarlo.",
  "None of these were really sent to CardTrader: they were only recorded.":
    "Nada de esto se envió de verdad a CardTrader: solo se registró.",
  "1 of these copies was only recorded, never really sent to CardTrader.":
    "1 de esas copias solo se registró: nunca se envió de verdad a CardTrader.",
  "{copies} of these copies were only recorded, never really sent to CardTrader.":
    "{copies} de esas copias solo se registraron: nunca se enviaron de verdad a CardTrader.",

  // --- write-back: the line on a shop's trade (ADR 0011) --------------------
  "Waiting to be taken off CardTrader.": "Pendiente de descontar en CardTrader.",
  "Not taken off CardTrader: updating your stock there is switched off.":
    "No se ha descontado en CardTrader: tienes desactivado actualizar tu stock allí.",
  "Sent to CardTrader without an answer. We are checking whether it came off.":
    "Enviadas a CardTrader sin respuesta. Estamos comprobando si se descontaron.",
  "Taken off CardTrader.": "Copias descontadas en CardTrader.",
  "Already sold on CardTrader: there was nothing left to take off. The trade is still on — decide it with the trader.":
    "Ya vendidas en CardTrader: no quedaba ninguna copia que descontar. El cambio sigue en pie; decidid entre los dos qué hacer.",
  "Already sold on CardTrader: only {taken} of {asked} copies were left to take off. The trade is still on — decide it with the trader.":
    "Ya vendidas en CardTrader: solo quedaban {taken} de {asked} copias que descontar. El cambio sigue en pie; decidid entre los dos qué hacer.",
  "Could not be taken off CardTrader: {why}. The copies are still on sale there, so take them off by hand.":
    "No se han podido descontar en CardTrader: {why}. Las copias siguen a la venta allí, así que retíralas a mano.",
  "Waiting to be put back on CardTrader.": "Pendiente de devolver a CardTrader.",
  "Sent back to CardTrader without an answer. We are checking whether it arrived.":
    "Enviadas de vuelta a CardTrader sin respuesta. Estamos comprobando si llegaron.",
  "Put back on CardTrader.": "Copias devueltas a CardTrader.",
  "Put back on CardTrader as a new product. Check its description there: it did not come back.":
    "Devueltas a CardTrader como producto nuevo. Revisa allí su descripción: esa no ha vuelto.",
  "Not put back on CardTrader: {why}. Put the copies back there by hand.":
    "No se han devuelto a CardTrader: {why}. Vuelve a poner las copias allí a mano.",
  "Only recorded: this was never sent to CardTrader.":
    "Solo registrado: esto nunca se envió a CardTrader.",
  // The reasons, lowercase: they finish a sentence.
  "CardTrader no longer accepts your token": "CardTrader ya no acepta tu token",
  "CardTrader was not answering": "CardTrader no respondía",
  "CardTrader did not answer in time": "CardTrader no respondió a tiempo",
  "the product is no longer on CardTrader": "el producto ya no está en CardTrader",
  "you disconnected CardTrader": "desconectaste CardTrader",
  "updating your CardTrader stock was switched off": "se desactivó actualizar tu stock de CardTrader",
  "something went wrong at our end": "algo falló por nuestra parte",
  "We cannot store a credential safely right now, so we did not take it.":
    "Ahora mismo no podemos guardar una credencial de forma segura, así que no la hemos cogido.",
  // "o mejor", which is what a floor means. Without it the row reads as an
  // exact condition and claims the opposite of the rule.
  "{grade} or better": "{grade} o mejor",
  "this printing only": "solo esta edición",
  // "Sin foil" rather than "no foil": the trader is naming which copy they want,
  // not negating one. "Solo normal" was the other candidate and reads as a
  // judgement about foils.
  "Non-foil only": "Solo sin foil",
  "Only this exact printing": "Solo esta edición exacta",
  "How this price follows Cardmarket. It is read and applied every night.":
    "Cómo sigue este precio a Cardmarket. Se lee y se aplica cada noche.",
  "Switch it off to go back to typing the price yourself. It stays where it last landed.":
    "Desactívalo para volver a escribir el precio tú. Se queda donde se quedó la última vez.",
  "See how this price follows Cardmarket": "Ver cómo sigue este precio a Cardmarket",
  "See how the price of {card} follows Cardmarket": "Ver cómo sigue a Cardmarket el precio de {card}",
  "Edit {card}": "Editar {card}",
  "The condition, the price, how many copies you have, your notes and the photographs.":
    "El estado, el precio, cuántas copias tienes, tus notas y las fotos.",
  "The condition, how many copies you have, your notes and the photographs.":
    "El estado, cuántas copias tienes, tus notas y las fotos.",
  "Trades already agreed keep the condition they were agreed at.":
    "Los cambios ya acordados mantienen el estado con el que se acordaron.",
  "{n} unread": "{n} sin leer",
  "{n} waiting on you": "{n} esperándote",
  // "Idi." is not a word. The column is narrow and "Idioma" fits where the
  // English abbreviation was needed, so Spanish gets the whole word.
  "Lang.": "Idioma",
  // Reads as a statement about the row rather than an instruction: it is a
  // tooltip on a mark, answering "why can I not type here".
  "This price follows Cardmarket": "Este precio sigue a Cardmarket",
  "You can stop at any time. The price stays where it last landed and is yours to edit again.":
    "Puedes dejarlo cuando quieras. El precio se queda donde cayó por última vez y vuelve a ser tuyo para editarlo.",
  // The verb alone; the wordmark beside it is the object, exactly as on the
  // listing page. "Seguir" on its own reads as unfinished, which it is.
  "Follow": "Seguir",
  // One word, like its English. The row's own label already asked the question.
  "Here": "Aquí",
  "Move the pin": "Mover el punto",
  // "Poner" rather than "colocar": plainer, and it is what a person says about
  // a pin on a map.
  "Put the pin on my address": "Poner el punto en mi dirección",
  // "Looking…" is NOT repeated here: it is already defined above for the
  // place search, with the same Spanish and the same meaning.
  "The pin is on your address now.": "El punto ya está en tu dirección.",
  // Says what to do next rather than only what failed: an address a geocoder
  // has never heard of is ordinary, and the pin is still droppable by hand.
  "We could not find that address. Drop the pin instead.":
    "No hemos encontrado esa dirección. Pon el punto a mano.",
  // "Aquí" first and alone on the line: it is the ordinary answer, and the one
  // a shop that has not thought about this should land on.
  "If your real stock is on another marketplace, we say so on your listings — a card sold there can still be showing here until the next import.":
    "Si tu stock de verdad está en otro sitio, lo decimos en tus anuncios: una carta vendida allí puede seguir apareciendo aquí hasta la siguiente importación.",
  // "Avisa antes de que alguien se desplace" is the whole job of this sentence,
  // so the verb a buyer must act on — comprobar — leads the second half.
  "This shop keeps its stock on {market}. A card sold there can still be showing here, so check with them before you travel.":
    "Esta tienda tiene su stock en {market}. Una carta vendida allí puede seguir apareciendo aquí, así que compruébalo con ellos antes de desplazarte.",
  // "Vende también en" rather than "también vende en": the subject is the
  // trader, already named above the line, so the verb leads.
  // The name itself is now the wordmark beside this, so the sentence stops
  // where the logo begins. "Vende también en" reads as an unfinished clause in
  // isolation, which is correct: it is finished by the mark.
  "Also sells on": "Vende también en",
  // "Cardmarket username" is NOT repeated here: the shop application form
  // already defines it further down, with the same Spanish. The English
  // sentence is the key on this project, so a second copy would be a dead
  // entry whose words the later one silently overrides.
  // "Lo que vendes" rather than "tu stock": the sentence is about a buyer
  // curious what else this person has, and "stock" is a seller's word.
  "Your name on Cardmarket, so buyers can see the rest of what you sell. Shown on your listings while it is set.":
    "Tu nombre en Cardmarket, para que quien compre pueda ver lo demás que vendes. Se muestra en tus anuncios mientras esté puesto.",
  // Mirrors the Telegram refusal beside it in shape and in specificity: it says
  // what a valid one looks like rather than only that this one is not.
  "That does not look like a Cardmarket username. Three characters or more, letters, numbers, hyphens and underscores.":
    "Eso no parece un usuario de Cardmarket. Tres caracteres o más, con letras, números, guiones y guiones bajos.",
  "Show this on my listings": "Mostrarlo en mis cartas",
  "Add one above to be able to show it": "Añade uno arriba para poder mostrarlo",
  "A username, not a phone number — which is why we suggest this one first.":
    "Un usuario, no un teléfono. Por eso te sugerimos este primero.",
  "WhatsApp": "WhatsApp",
  "This is your phone number. Anybody signed in can see it on your listings while the switch is on.":
    "Es tu número de teléfono. Cualquiera que haya iniciado sesión puede verlo en tus cartas mientras el interruptor esté activado.",
  "Signing in is required to see a contact, and the app's own messages still work either way — they are what a trade and a report are built on.":
    "Hay que iniciar sesión para ver un contacto, y los mensajes de la app siguen funcionando igual: son la base de un intercambio y de una denuncia.",
  "Add the country code to the phone, like +34 968 12 34 56.":
    "Añade el prefijo de país al teléfono, por ejemplo +34 968 12 34 56.",
  "That does not look like a phone number.": "Eso no parece un número de teléfono.",
  "With the country code, so anybody can ring it.":
    "Con el prefijo de país, para que cualquiera pueda llamar.",
  "Add your country code, like +34 600 11 22 33 or +39 333 123 4567.":
    "Añade tu prefijo de país, por ejemplo +34 600 11 22 33 o +39 333 123 4567.",
  "That does not look like a phone number. Include the country code, like +34 600 11 22 33.":
    "Eso no parece un número de teléfono. Incluye el prefijo del país, como +34 600 11 22 33.",
  "That does not look like a Telegram username. They are five characters or more, letters, numbers and underscores.":
    "Eso no parece un usuario de Telegram. Son cinco caracteres o más, con letras, números y guiones bajos.",
  "That could not be saved.": "No se ha podido guardar.",
  "Also reachable on": "También en",
  "That trader is no longer reachable there.": "Esa persona ya no está disponible ahí.",
  "Sign in to see how to reach this trader.": "Inicia sesión para ver cómo contactar con esta persona.",
  "Confirm your email address to see how to reach traders. The app's own messages work either way.":
    "Confirma tu correo para ver cómo contactar con otras personas. Los mensajes de la app funcionan igualmente.",
  "That could not be loaded.": "No se ha podido cargar.",
  "Browse opens on these. You can still search any game.": "Explorar se abre con estos. Puedes buscar en cualquier juego igualmente.",
  /*
   * What an EMPTY selection looks like, on the games row this time. Not a game
   * and not a stored value — the `game` enum has five members and none of them
   * is "todos", because a card belongs to one of them.
   *
   * "Todos los juegos", masculine plural agreeing with "juegos": the chip sits
   * in a row of game names and is one of them grammatically, whatever it means.
   * The twin of "Todos los idiomas" below, and deliberately the same shape —
   * the two controls sit side by side and should read as the same question
   * asked twice.
   *
   * It replaces "No has elegido ninguno", which stated the same fact as an
   * absence: a setting that sounds unfinished rather than answered.
   */
  "All games": "Todos los juegos",
  /*
   * The chip that reveals the rest of a list — the games and the card languages
   * both use it. Distinct from "Show more" on the browse feed, which loads
   * another page from the server: this one shows what is already here, and the
   * ellipsis is the difference a reader can see.
   *
   * "Ver más…" rather than "Mostrar más": the person is looking, not commanding
   * the list, and it is the shorter of the two on a chip that has to fit beside
   * three others on a phone.
   */
  "Show more…": "Ver más…",
  "All games, so browse opens on everything.":
    "Todos los juegos, así que Explorar se abre con todo.",
  "Those games could not be saved.": "No se han podido guardar esos juegos.",
  "Emails": "Correos",
  "At most one a day, and only about things you have not already seen in the app.": "Como mucho uno al día, y solo sobre cosas que no hayas visto ya en la app.",
  "A card I want turns up nearby": "Aparece cerca una carta que busco",
  "Somebody posts a card on your wants list, inside your radius": "Alguien publica una carta de tu lista, dentro de tu radio",
  "Somebody writes to me": "Alguien me escribe",
  "A message you have not read": "Un mensaje que no has leído",
  "Somebody is waiting on my answer": "Alguien espera mi respuesta",
  "A trade proposed to you and still unanswered": "Un cambio que te han propuesto y sigue sin responder",
  "On this device": "En este dispositivo",
  "A notification on your phone or computer, straight away. Emails still arrive as well.": "Un aviso en el móvil o el ordenador, al momento. Los correos siguen llegando igual.",
  "Notify this device": "Avisar en este dispositivo",
  "Blocked by your browser. Allow notifications for this site to turn it on.": "Bloqueado por el navegador. Permite los avisos para este sitio para activarlo.",
  "Your browser is blocking notifications for this site. You can allow them in its settings.": "El navegador está bloqueando los avisos de este sitio. Puedes permitirlos en sus ajustes.",
  "Push notifications are not set up on this server yet.": "Los avisos en el dispositivo aún no están configurados en este servidor.",
  "The permission request was closed without an answer. Try again.": "Has cerrado la petición de permiso sin responder. Vuelve a intentarlo.",
  "Notifications could not be turned on: {reason}": "No se han podido activar los avisos: {reason}",
  "Sent as soon as something happens, and grouped so a busy afternoon is still one email.": "Se envían en cuanto pasa algo, y se agrupan para que una tarde movida siga siendo un solo correo.",
  "Confirm your email address before we can write to you. Nothing is sent to an address nobody has confirmed.": "Confirma tu correo para que podamos escribirte. No se envía nada a una dirección sin confirmar.",
  // "Send it again" and "Email" both used to be written twice in this file, once
  // here and once in the verification and account sections below. A repeated key
  // in an object literal is not two entries — the later one silently wins — so
  // every reader of this file saw a translation that was never rendered, and Vite
  // warned about it on every build. The surviving copy of each is the one that was
  // actually winning, so nothing on screen changes; the entries are down there
  // because that is where the sentences around them live.
  "Notifications": "Avisos",
  "Sent as soon as something happens, and grouped so a busy afternoon is still one message.": "Se envían en cuanto pasa algo, y se agrupan para que una tarde movida siga siendo un solo mensaje.",
  "Tell me when": "Avísame cuando",
  "Device": "Dispositivo",
  "Make a sound for new messages": "Sonar cuando llegue algo nuevo",
  "A short chime when something arrives while you are looking at another tab": "Un aviso corto cuando llega algo mientras estás en otra pestaña",
  "That setting could not be saved.": "No se ha podido guardar ese ajuste.",
  "Add photos": "Añadir fotos",
  "Photos of your card": "Fotos de tu carta",
  "At least one. Front, back, and a close-up of any wear — this is what somebody is judging.": "Al menos una. Anverso, reverso y un primer plano del desgaste — es lo que la otra persona va a juzgar.",
  "Add another": "Añadir otra",
  "Finding listings within {radius}.": "Buscando anuncios a {radius}.",
  "No photo? It still posts. Cards with one are the ones people filter for.": "¿Sin foto? Se publica igual. Las cartas con foto son las que la gente filtra.",
  "One copy.": "Una copia.",
  "{quantity} copies.": "{quantity} copias.",
  "{available} of {quantity} free — {committed} promised to trades already agreed.": "{available} de {quantity} libres — {committed} comprometidas en intercambios ya acordados.",
  "Choose the game to post it.": "Elige el juego para publicarla.",
  // The language OF THE CARD, said out loud in the sentence, because "elige el
  // idioma" on a screen with a language switch in its header reads as the
  // interface's.
  "Choose the language printed on the card to post it.":
    "Elige el idioma impreso en la carta para publicarla.",
  // The printing, since 2026-10-12: a title on its own does not post. One
  // sentence with its reason, beside the way out. "Colección" because that is
  // what the report it points at calls a set.
  "Pick the card from the list to post it: a card we do not have cannot be priced, pictured or matched, so tell us which set is missing instead.":
    "Elige la carta en la lista para publicarla: una carta que no tenemos no se puede valorar, ilustrar ni emparejar, así que dinos qué colección falta.",
  "Show more": "Ver más",
  "Loading…": "Cargando…",
  "Something went wrong on our side. Try again in a moment.": "Algo ha fallado por nuestra parte. Inténtalo dentro de un momento.",
  "Photos ({n})": "Fotos ({n})",
  "Photos of “{card}”": "Fotos de “{card}”",
  "Your own pictures of this copy. They are what somebody is actually judging — the catalogue picture is the same for every copy in print.": "Tus propias fotos de esta copia. Son lo que alguien juzga de verdad — la foto del catálogo es la misma para todas las copias impresas.",
  "No photos yet": "Aún no hay fotos",
  "Front, back, and a close-up of anything a buyer would want to see.": "Anverso, reverso y un primer plano de lo que un comprador querría ver.",
  "Add a photo": "Añadir una foto",
  "Uploading…": "Subiendo…",
  "{n} of {max} left": "Quedan {n} de {max}",
  "That is the most a listing can carry.": "Es el máximo que admite un anuncio.",
  "Photos are stripped of their hidden data — including the location your phone records — before anyone else can see them.": "A las fotos se les quitan los datos ocultos — incluida la ubicación que guarda tu móvil — antes de que nadie más las vea.",
  "Remove this photo": "Quitar esta foto",
  "Photo added.": "Foto añadida.",
  "{n} photos added.": "{n} fotos añadidas.",
  "That photo could not be removed.": "No se ha podido quitar esa foto.",
  "“{name}” is too big. 12 MB is the limit.": "“{name}” es demasiado grande. El límite son 12 MB.",
  "“{name}” is not a photo we can read.": "“{name}” no es una foto que podamos leer.",
  "“{name}” did not upload.": "“{name}” no se ha subido.",
  "Photo uploads are switched off right now.": "La subida de fotos está desactivada ahora mismo.",
  "Photo {n}": "Foto {n}",
  "The publisher’s picture": "La foto del editor",

  // --- a listing's picture, opened ------------------------------------------
  // "Ampliar" rather than "Agrandar": it is the word Spanish photo viewers use
  // for this, and the one on the phone gallery every trader already has.
  "Enlarge the picture": "Ampliar la imagen",
  "Close the picture": "Cerrar la imagen",
  // "Acercar" is what a Spanish phone gallery's zoom control says, and the pair
  // reads as one control changing state rather than as two unrelated words.
  "Zoom in": "Acercar",
  "Fit the picture to the window": "Ajustar la imagen a la ventana",
  "Previous picture": "Imagen anterior",
  "Next picture": "Imagen siguiente",
  "Picture {n} of {total}": "Imagen {n} de {total}",
  // The name a screen reader announces when the viewer opens. A comma rather
  // than a dash, which is read aloud in Spanish as a pause and not as a word.
  "{title}, full size": "{title}, a tamaño completo",

  "The seller’s own photo of this copy. Check the card at the meetup before you pay.": "La foto del propio vendedor de esta copia. Comprueba la carta en la quedada antes de pagar.",
  "Places within {km} km of you.": "Sitios a menos de {km} km de ti.",
  "Nothing by that name within {km} km of you. Check the spelling, or just type where you want to meet \u2014 the name is yours to write.": "No hay nada con ese nombre a menos de {km} km de ti. Revisa c\u00f3mo est\u00e1 escrito, o escribe directamente d\u00f3nde quieres quedar \u2014 el nombre lo pones t\u00fa.",
  "Nothing found by that name. You can type where you want to meet instead.": "No se ha encontrado nada con ese nombre. Puedes escribir directamente d\u00f3nde quieres quedar.",
  // --- a picture that is not the card ---------------------------------------
  // "Version" is Cardmarket's own word and the one the posting form already
  // uses, so a trader reporting a wrong picture reads the same term they chose
  // the card with.
  "This is not the right picture": "Esta no es la imagen correcta",
  "Tell us the picture is missing": "Av\u00edsanos de que falta la imagen",
  "Wrong picture for this card": "Imagen incorrecta para esta carta",
  "No picture for this card": "No hay imagen para esta carta",
  "The picture we show for {code} does not match the card. Tell us and somebody will look.": "La imagen que mostramos para {code} no coincide con la carta. Av\u00edsanos y alguien la revisar\u00e1.",
  /*
   * The same two sentences without a card code, for the four games that have
   * none. A printing is what the picture belongs to; naming it "esta impresión"
   * rather than inventing an identifier the reader has never seen.
   */
  "We have no picture for this printing. Telling us means somebody can find one.":
    "No tenemos imagen de esta impresión. Si nos lo dices, alguien podrá buscarla.",
  "The picture we show for this printing does not match the card. Tell us and somebody will look.":
    "La imagen que mostramos de esta impresión no coincide con la carta. Dínoslo y alguien la revisará.",
  "We have no picture for {code}. Telling us means somebody can find one.": "No tenemos imagen para {code}. Si nos avisas, alguien podr\u00e1 buscarla.",
  "Anything that helps (optional)": "Cualquier cosa que ayude (opcional)",
  "For example: this is the alternate art, mine has the normal border.": "Por ejemplo: esta es la ilustraci\u00f3n alternativa, la m\u00eda tiene el borde normal.",
  "Thank you \u2014 we will check this picture.": "Gracias \u2014 revisaremos esta imagen.",
  "That did not send. Try again in a moment.": "No se ha podido enviar. Int\u00e9ntalo de nuevo en un momento.",

  // --- answering a proposal with a different one ----------------------------
  "Offer something else": "Ofrecer otra cosa",
  "Offer something different": "Ofrece otra cosa",
  "They asked for this trade. Change what you put up, and it goes back to them to answer.": "Te han propuesto este intercambio. Cambia lo que pones t\u00fa y vuelve a ellos para que respondan.",
  "You get": "Recibes",
  "You give": "Entregas",
  "Send it back": "Devolver la propuesta",
  "Change a card or the cash to send this back.": "Cambia una carta o el dinero para devolver la propuesta.",
  "On its own this is a price counter \u2014 same cards, different money.": "Por s\u00ed solo esto es una contraoferta de precio \u2014 las mismas cartas, distinto dinero.",
  "You have nothing listed to offer. Post a card first, or add cash below.": "No tienes nada publicado que ofrecer. Publica una carta primero, o a\u00f1ade dinero abajo.",
  "Cash, if any": "Dinero, si lo hay",
  "I pay": "Pago yo",
  "They pay": "Pagan ellos",
  "Sent back with your offer. It is their turn now.": "Devuelta con tu oferta. Ahora les toca a ellos.",

  "Open in maps": "Abrir en el mapa",
  "Filter your cards": "Filtra tus cartas",
  "No set named": "Sin colecci\u00f3n indicada",
  "View": "Ver",
  "Nothing matches that.": "No hay nada que coincida.",

  "Foil or not": "Con o sin foil",
  "Only foils": "Solo foil",
  "No foils": "Sin foil",

  "Report a problem": "Informar de un problema",
  "Help": "Ayuda",
  "Tell us what happened": "Cu\u00e9ntanos qu\u00e9 ha pasado",
  "Bugs, ideas, anything. It reaches a person, not a queue.": "Fallos, ideas, lo que sea. Lo lee una persona, no una cola.",
  "What is this?": "\u00bfDe qu\u00e9 se trata?",
  "Something is broken": "Algo no funciona",
  "An idea": "Una idea",
  "What happened?": "\u00bfQu\u00e9 ha pasado?",
  "What you did, what you expected, what you got instead.": "Qu\u00e9 hiciste, qu\u00e9 esperabas y qu\u00e9 pas\u00f3 en su lugar.",
  "Email, if you would like an answer (optional)": "Correo, si quieres respuesta (opcional)",
  "We also record which page you were on and which browser you are using, so nobody has to describe them.": "Tambi\u00e9n guardamos en qu\u00e9 p\u00e1gina estabas y qu\u00e9 navegador usas, para que no tengas que describirlo.",
  "Thank you \u2014 we have it.": "Gracias \u2014 lo tenemos.",
  "You appear to be offline. Nothing was sent.": "Parece que est\u00e1s sin conexi\u00f3n. No se ha enviado nada.",
  "Tell me when a want turns up": "Av\u00edsame cuando aparezca algo que busco",
  "Shows a note on Browse when something on your wants list is inside your radius": "Muestra un aviso en Explorar cuando algo de tu lista est\u00e1 dentro de tu radio",
  "Or any other card you have": "O cualquier otra carta que tengas",
  "What are you putting up?": "\u00bfQu\u00e9 pones t\u00fa?",
  "It does not have to be listed. Name what you are bringing \u2014 you hand it over in person either way.": "No hace falta que est\u00e9 anunciada. Di qu\u00e9 llevas \u2014 se la entregas en mano igualmente.",
  "You are offering": "Ofreces",
  "Remove": "Quitar",
  "Matches against your wants list": "Coincidencias con tus búsquedas",
  "Nothing within your radius yet": "Todavía no hay nada en tu radio",
  "No listings match these filters. Widen the radius or drop a filter — every extra kilometre usually adds a few cards.":
    "Ningún anuncio coincide. Amplía el radio o quita un filtro: cada kilómetro más suele añadir unas cuantas cartas.",
  // The rung is interpolated now: this said "Widen to 15 km" on every radius,
  // which did nothing at 15 km and narrowed you at 50.
  "Widen to {radius}": "Ampliar a {radius}",
  "No listings match these filters. {radius} is as wide as the radius goes, so try dropping one.":
    "Ningún anuncio coincide. {radius} es el radio más amplio, así que prueba a quitar un filtro.",
  "Nothing is listed within {radius} of you yet, and that is as wide as the radius goes.":
    "Todavía no hay nada anunciado a {radius} de ti, y es el radio más amplio.",
  "Clear filters": "Quitar filtros",
  "Can't find it?": "¿No la encuentras?",
  "Add it to your wants and we'll alert you when it appears within your radius.":
    "Añádela a tus búsquedas y te avisamos cuando aparezca en tu radio.",
  "Add a want": "Añadir búsqueda",
  "Show them": "Verlas",

  // --- listing --------------------------------------------------------------
  "Back to browse": "Volver a explorar",
  "Message": "Mensaje",
  "Propose a trade": "Proponer canje",
  "That listing is gone": "Ese anuncio ya no está",
  "It may have been traded or withdrawn. There are others nearby.":
    "Puede que ya se haya cambiado o lo hayan retirado. Hay más cerca de ti.",
  // A listing that is down, opened by its link — from an agreed trade, or an
  // old share. It says so and offers nothing that starts a deal (ticket 12 of
  // the CardTrader write-back). "Retirado" as "Anuncio retirado" says it.
  "Withdrawn": "Retirado",
  "Already traded": "Ya cambiado",
  "This listing has been taken down. It takes no new proposals, and a trade already agreed on it still stands.":
    "Este anuncio está retirado. No admite propuestas nuevas, y un canje ya acordado sobre él sigue en pie.",
  "This card has already been traded. It takes no new proposals.":
    "Esta carta ya se ha cambiado. No admite propuestas nuevas.",
  "Browse nearby cards": "Ver cartas cerca de ti",
  "What are you offering?": "¿Qué ofreces?",
  // Under that question: the card you are offering AGAINST. "A cambio de" rather
  // than a literal "Para" — you are proposing something in exchange for it, which
  // is what the preposition has to carry here.
  //
  // {price} arrives already formatted, so the symbol is on the end where a
  // Spanish reader expects it and the sentence never has to know that. {condition}
  // is the grade notation (NM, EX) — the trade's own, the same in both languages.
  "For {title} · {condition} · {price} asked": "A cambio de {title} · {condition} · piden {price}",
  "For {title} · {condition}": "A cambio de {title} · {condition}",
  "Where and when?": "¿Dónde y cuándo?",
  "Your cards": "Tus cartas",
  "Cash on top (optional)": "Dinero encima (opcional)",
  "Pick a card, add cash, or both.": "Elige una carta, añade dinero, o las dos cosas.",
  "You have not posted any cards yet. You can still offer cash.":
    "Todavía no has publicado ninguna carta. Aun así puedes ofrecer dinero.",
  "Next": "Siguiente",
  "Send proposal": "Enviar propuesta",
  "Sending…": "Enviando…",
  "Saving…": "Guardando…",
  "You send": "Tú envías",
  "Nothing yet": "Nada todavía",
  "Meeting point": "Punto de encuentro",
  "How to find it": "Cómo llegar",
  "When": "Cuándo",
  "Find it": "Buscar",
  "Looking…": "Buscando…",

  // --- copies: a listing is a number of cards, not one card -----------------
  // "Copia" rather than "unidad": traders talk about copies of a card, and a
  // unit is what a warehouse has.
  /* The price suggestion. The label carries the source and the window and
     nothing else — a caveat nobody asked for reads as an apology for the
     number. */
  "What this printing usually goes for": "Por cuánto se suele vender esta impresión",
  "Sold here · 7-day average": "Vendido aquí · media de 7 días",
  "Cardmarket · 7-day average": "Cardmarket · media de 7 días",
  "Cardmarket · low": "Cardmarket · bajo",
  "Cardmarket · average": "Cardmarket · medio",
  /* Short, because the box beside the price is 104px and a quantity is one or
     two digits. "Copias" is the word the rest of this screen uses. */
  /* Editing a listing's price. The hint is the reassurance a seller needs
     before touching it: the deal somebody already agreed to does not move. */
  "Notes, price and how many copies you have. The condition cannot change — take it down and post again for that.":
    "Las notas, el precio y cuántas copias tienes. El estado no se puede cambiar: quítalo y vuelve a publicarlo para eso.",
  "Notes and how many copies you have. The condition cannot change — take it down and post again for that.":
    "Las notas y cuántas copias tienes. El estado no se puede cambiar: quítalo y vuelve a publicarlo para eso.",
  "Give a price above 0€.": "Pon un precio mayor que 0 €.",
  "Trades already agreed keep the price they were agreed at.":
    "Los cambios ya acordados mantienen el precio que tenían al acordarse.",
  /* Un anuncio que trae la sincronización con CardTrader: todo él es de allí
     (ticket 11), y aquí solo se enseña. Lo único que se hace aquí es retirarlo. */
  "This listing is managed on CardTrader. Change it there and it updates here within minutes.":
    "Este anuncio se gestiona en CardTrader. Cámbialo allí y aquí se actualiza en unos minutos.",
  "Listing details": "Detalles del anuncio",
  "Details": "Detalles",
  "See {card}": "Ver {card}",
  /* Añadida tras el mensaje de un cambio en bloque, cuando ha dejado algunos aparte. */
  "{skipped} are managed on CardTrader, so their prices stay as they are.":
    "{skipped} se gestionan en CardTrader, así que sus precios se quedan como están.",
  "Managed on CardTrader": "Se gestiona en CardTrader",
  "Synchronized with CardTrader": "Sincronizado con CardTrader",
  "Synced": "Sincronizados",
  "Synced cards take their price from CardTrader, so it is changed there.":
    "Los sincronizados toman su precio de CardTrader, así que se cambia allí.",
  "Agreed at {total}": "Acordado en {total}",
  "Copies": "Copias",
  /* The listing card's one action, and the photo section now inside the edit
     dialog. "Editar" alone, because the card it sits under says which listing. */
  "Edit": "Editar",
  "Your photos": "Tus fotos",
  "Your own pictures of this copy. They save as you add them.":
    "Tus propias fotos de esta copia. Se guardan según las añades.",

  /* A listing whose price follows Cardmarket, per listing — migración 091.
     "El precio bajo" rather than a literal "precio mínimo": it is Cardmarket's
     own `low`, the cheapest copy on offer, and a seller comparing the two
     screens should recognise the same idea. The switch is written as something
     the price DOES ("sigue"), not as a setting that was configured. */
  "Follow Cardmarket's price": "Seguir el precio de Cardmarket",
  "Updated every day from Cardmarket.": "Se actualiza cada día con Cardmarket.",
  "Cardmarket sets this price now. Switch that off below to type your own.":
    "Ahora este precio lo pone Cardmarket. Desactívalo abajo para escribir el tuyo.",
  /* Which of Cardmarket's two numbers a listing follows — migración 092. The
     same words the posting form's own suggestions use, so the two screens name
     the same thing the same way. */
  "Cardmarket sets this price.": "Este precio lo pone Cardmarket.",
  "Which price to follow": "Qué precio seguir",
  "Cardmarket's low price": "El precio bajo de Cardmarket",
  "Cardmarket's average price": "El precio medio de Cardmarket",
  "What to charge": "Qué cobrar",
  /* "Ese precio", not "el precio bajo": these sit under the selector that says
     WHICH price, and naming a basis here contradicts it whenever the seller has
     chosen the other one. */
  "That price, as it is": "Ese precio, tal cual",
  "That price, adjusted by a percentage": "Ese precio, ajustado un porcentaje",
  "That price, adjusted by an amount": "Ese precio, ajustado una cantidad",
  "Percentage": "Porcentaje",
  "Give a percentage between -99 and 1000.": "Pon un porcentaje entre -99 y 1000.",
  /* The minus sign is the whole point of the field, so the example carries it
     rather than describing it. */
  "Start with a minus to sell below the market: -5 means 5% cheaper.":
    "Empieza con un menos para vender por debajo del mercado: -5 significa un 5% más barato.",
  "Direction": "Dirección",
  "Above that price": "Por encima de ese precio",
  "Below that price": "Por debajo de ese precio",
  "Amount": "Cantidad",
  "Give an amount above 0€.": "Pon una cantidad mayor que 0 €.",
  "If Cardmarket has no price for this card, yours stays as it is.":
    "Si Cardmarket no tiene precio para esta carta, el tuyo se queda como está.",
  /* `{price}`, not `{low}`: it renders whichever basis the seller chose. */
  "Cardmarket: {price}": "Cardmarket: {price}",
  "You charge {price}": "Tú cobras {price}",

  /* Setting market-following across a whole inventory. "Seguir" is the verb the
     per-listing switch already uses, so the two screens name the same thing the
     same way. */
  "Set this across your listings at once. You can still change any of them on its own afterwards.":
    "Aplícalo a tus anuncios de una vez. Después puedes cambiar cualquiera por separado.",
  "Which listings": "Qué anuncios",
  "All my listings": "Todos mis anuncios",
  "Only the ones I pick": "Solo los que elija",
  "Select all": "Seleccionar todos",
  "Clear": "Quitar la selección",
  "Not in the catalogue": "No está en el catálogo",
  "Apply to all {n} listings": "Aplicar a los {n} anuncios",
  "Apply to {n} listings": "Aplicar a {n} anuncios",
  "Cards Cardmarket has no price for keep the price you set.":
    "Las cartas de las que Cardmarket no tiene precio mantienen el que tú pongas.",
  "{n} listings now follow Cardmarket.": "{n} anuncios siguen ahora a Cardmarket.",
  "{n} listings no longer follow Cardmarket.": "{n} anuncios ya no siguen a Cardmarket.",
  "{n} listings now follow Cardmarket. {skipped} are not in the catalogue, so their prices stay as they are.":
    "{n} anuncios siguen ahora a Cardmarket. {skipped} no están en el catálogo, así que sus precios se quedan como están.",

  // Meeting at a game shop, which is the neutral ground a trade between two
  // strangers actually wants.
  "Meet at a game store": "Quedar en una tienda",
  "{shop} (theirs)": "{shop} (la suya)",
  "A game store is somewhere public, indoors and open to everyone.":
    "Una tienda es un sitio p\u00fablico, a cubierto y abierto a todo el mundo.",
  // A verified local game store.
  "Verified store": "Tienda verificada",
  "Their website": "Su web",
  // Searching one seller's shelf.
  "Search their cards": "Busca en sus cartas",
  "Card or set": "Carta o colecci\u00f3n",
  "Any": "Cualquiera",
  "Any language": "Cualquier idioma",
  "Sale or trade": "Venta o cambio",
  "Foil only": "Solo foil",
  "No foil": "Sin foil",
  "Only cards I want": "Solo cartas que busco",
  "Only cards with a photo": "Solo con foto",
  "Nothing here matches that": "Aqu\u00ed no hay nada que encaje",
  "They have cards up, just none matching this search. Try fewer filters.":
    "Tiene cartas publicadas, pero ninguna encaja con esta b\u00fasqueda. Prueba con menos filtros.",
  "Clear the filters": "Quitar los filtros",
  // ── A trader's own page ────────────────────────────────────────────────────
  "No trader by that name": "No hay ning\u00fan usuario con ese nombre",
  "The link may be old, or they may have closed their account.":
    "Puede que el enlace sea antiguo, o que haya cerrado su cuenta.",
  "Your listings": "Tus anuncios",
  "Trading here since {month}": "Aqu\u00ed desde {month}",
  "Reported by other traders": "Denunciado por otros usuarios",
  "Nothing for sale right now": "Ahora mismo no tiene nada",
  "They have no cards up at the moment. Message them if you are after something in particular.":
    "No tiene ninguna carta publicada. Escr\u00edbele si buscas algo en concreto.",
  "Their cards could not be loaded": "No se han podido cargar sus cartas",
  "Loading their cards.": "Cargando sus cartas.",
  "See everything they have": "Ver todo lo que tiene",
  /*
   * Month names, translated here rather than taken from `Intl`.
   *
   * The interface's language and the browser's are two different questions, and
   * this product answers the first: somebody reading the site in Spanish on an
   * English phone should not get "March".
   *
   * Lowercase, because Spanish does not capitalise them.
   */
  "January": "enero",
  "February": "febrero",
  "March": "marzo",
  "April": "abril",
  "May": "mayo",
  "June": "junio",
  "July": "julio",
  "August": "agosto",
  "September": "septiembre",
  "October": "octubre",
  "November": "noviembre",
  "December": "diciembre",
  // The proposal form's money box. «Cash on top» stays for a card offered for
  // trade, where there is no asking price and the money really is an extra.
  "What you pay": "Lo que pagas",
  "They are asking {price}. Offer what you like \u2014 they can answer with a different number.":
    "Pide {price}. Ofrece lo que quieras \u2014 puede responder con otra cifra.",
  // What a counter moved. "Dropped" is the card that left the offer, not one
  // somebody dropped on the floor — «Quita» reads as the action it was.
  "What you changed": "Lo que has cambiado",
  "What they changed": "Lo que ha cambiado",
  "Cash": "Dinero",
  "Added": "A\u00f1ade",
  "Dropped": "Quita",
  "you pay {amount}": "pagas {amount}",
  "they pay {amount}": "paga {amount}",
  "no cash": "sin dinero",
  /* The three words for one thing, in the label rather than the small print:
     somebody scanning for the word THEY use should find it where they look. */
  "Foil / Holo / Shiny": "Foil / Holo / Brillante",
  "How many copies?": "¿Cuántas copias?",
  // Without the question mark: this one is a form label on the posting screen,
  // not a question asked of a buyer in a dialog.
  "How many copies": "Cuántas copias",
  "One card each is the usual answer. Say more if you have a playset.":
    "Lo normal es una de cada. Pon más si tienes un playset.",
  "{available} of {quantity} still free.": "{available} de {quantity} siguen libres.",
  "All {quantity} are promised to trades already agreed. You can still ask — the seller may free one up.":
    "Las {quantity} están comprometidas en cambios ya acordados. Aun así puedes pedirlas — puede que quien vende libere alguna.",
  "{available} of {quantity} available": "{available} de {quantity} disponibles",
  "All {quantity} spoken for": "Las {quantity} están comprometidas",
  "This asks for {asked} but only {left} are still free. Cancelling an accepted trade puts copies back.":
    "Esto pide {asked} y solo quedan {left} libres. Si cancelas un cambio aceptado, esas copias vuelven.",

  // --- trades ---------------------------------------------------------------
  "Every trade ends with two people in the same place. Confirm the time, then hand it over.":
    "Todo canje acaba con dos personas en el mismo sitio. Confirmad la hora y entregadla.",
  "Active": "Activos",
  "Proposed": "Propuestos",
  "Past": "Pasados",
  // The badge on a meetup card. MEETUP_STATUS_NAMES in data.jsx holds the keys;
  // the design system draws the badge and chooses its colour, and until it took a
  // `statusLabel` these four words were the only English left on the screen.
  "Awaiting reply": "Esperando respuesta",
  "Confirmed": "Confirmado",
  "Completed": "Completado",
  "Cancelled": "Cancelado",
  "Accept": "Aceptar",
  "Decline": "Rechazar",
  "Withdraw": "Retirar",
  "Mark done": "Marcar como hecho",
  "Report": "Denunciar",
  "Directions": "Cómo llegar",
  "Not agreed yet": "Sin acordar",
  "Time not agreed yet": "Hora sin acordar",
  "Accept and confirm": "Aceptar y confirmar",
  "One moment…": "Un momento…",
  // Answering a proposal that already names a place and a time. The dialog says
  // what the other person suggested and offers one tap; changing the details is
  // the quieter path beside it — and it is still an ACCEPT, on the terms in the
  // form, which is why the Spanish says so as plainly as the English does.
  "{name} suggested a meetup": "{name} ha propuesto una quedada",
  "Accepting fixes the trade on these terms. Nothing is charged through the app.":
    "Al aceptar, el intercambio queda fijado en estos t\u00e9rminos. Por la app no se cobra nada.",
  // Cash "on top" is the extra beside the cards, not the price of the trade.
  "You pay {amount} on top.": "Pones {amount} adem\u00e1s.",
  "They pay {amount} on top.": "Pone {amount} adem\u00e1s.",
  "Accept as offered": "Aceptar tal cual",
  "Change the details": "Cambiar los detalles",
  "Changing these accepts the trade on your terms. {name} is not asked again.":
    "Si los cambias, el canje se acepta en tus términos. A {name} no se le vuelve a preguntar.",
  "Where": "Dónde",
  "No meetups agreed yet": "Todavía no hay quedadas",
  "Nothing waiting on a reply": "Nada pendiente de respuesta",
  "No finished trades yet": "Todavía no hay canjes terminados",
  "Completed hand-offs stay here with what changed hands.":
    "Los canjes hechos se quedan aquí con lo que cambió de manos.",
  "Find a card within your radius and propose a swap — most replies come the same day.":
    "Busca una carta en tu radio y propón un canje.",
  "Meet in a public place, check condition against the photos before money changes hands, and mark the trade done together. Nothing is charged through the app.":
    "Quedad en un sitio público, revisad el estado antes de que cambie el dinero de manos y marcad el canje como hecho juntos. Por la app no se cobra nada.",
  "Pick somewhere public that suits you both. Nothing is charged through the app.":
    "Elegid un sitio público que os venga bien a los dos. Por la app no se cobra nada.",
  "Put it on the map (optional)": "Ponlo en el mapa (opcional)",

  // --- reporting ------------------------------------------------------------
  "They did not turn up": "No apareció",
  "The card was not as described": "La carta no era como la describía",
  "The card was fake": "La carta era falsa",
  "They behaved badly": "Se portó mal",
  "Something else": "Otra cosa",
  "What happened (optional)": "Qué pasó (opcional)",
  "One report per trade. It goes to us, not to them.":
    "Una denuncia por canje. Nos llega a nosotros, no a la otra persona.",
  "For when something actually went wrong. Changing your mind is not this — withdraw instead, and nothing happens to either of you.":
    "Para cuando algo ha salido mal de verdad. Cambiar de opinión no es esto: retírate y no pasa nada.",

  // --- inbox ----------------------------------------------------------------
  "Agree a place and a time. Everything else happens face to face.":
    "Acordad sitio y hora. Todo lo demás pasa en persona.",
  "No messages yet": "Todavía no hay mensajes",
  "Propose a trade and the conversation starts here.":
    "Propón un canje y la conversación empieza aquí.",
  "Suggest a place and a time": "Propón un sitio y una hora",
  "Send": "Enviar",
  // A listing link in a conversation, drawn as the listing. See listing-link-card.jsx.
  "Your listing": "Tu anuncio",
  "No longer available": "Ya no está disponible",
  "This listing no longer exists": "Este anuncio ya no existe",
  "Picture of the card": "Imagen de la carta",
  "Loading the listing.": "Cargando el anuncio.",
  "Will be sent with the listing card": "Se enviará con la ficha del anuncio",
  "Send only the link, without the card": "Enviar solo el enlace, sin ficha",
  // Was a second copy of "Loading…", for the one line of grey text this screen
  // used to answer an opening conversation with. The placeholders say what is
  // coming now, and the live region says which list it is — so the key that
  // replaces it is the inbox's own sentence rather than the shared word.
  "Loading the conversation.": "Cargando la conversación.",
  "Back to inbox": "Volver a mensajes",
  "You:": "Tú:",

  // --- post -----------------------------------------------------------------
  "Card": "Carta",
  /*
   * The set on a scanned listing. It arrives PRESELECTED and editable — it used
   * to arrive as a label nobody could change, which is wrong for the one person
   * holding the card. "Change it if it is wrong" is the whole point of the line.
   */
  "We filled this in from the card you picked. Change it if it is wrong.":
    "Lo hemos rellenado con la carta que has elegido. Cámbialo si no es correcto.",
  "The only set our catalogue holds this card in.":
    "La única colección en la que nuestro catálogo tiene esta carta.",
  "Which set is it printed in? It sets the picture and the printing a buyer's search matches.":
    "¿En qué colección está impresa? Decide la imagen y la edición que encuentra quien busca.",
  /*
   * "The set I am holding is not on your list" — a REPORT, not a text box. A
   * typed set went into the listing, where no filter matches it and no import
   * reconciles it, so the gap it described was invisible to us.
   */
  "My set is not on this list": "Mi colección no está en la lista",
  // The same report for a card we do not have at all, beside the post button.
  "My card is not on the list": "Mi carta no está en la lista",
  "Tell us what is printed on the card and we will add it to the catalogue.": "Dinos qué pone en la carta y la añadiremos al catálogo.",
  "A set we do not have": "Una colección que no tenemos",
  "Tell us what is printed on the card and we will add it to the catalogue. Your listing keeps the set you picked.":
    "Dinos qué pone en la carta y la añadiremos al catálogo. Tu anuncio conserva la colección que has elegido.",
  "Which set is it?": "¿Qué colección es?",
  "As it is printed on the card": "Tal como aparece impresa en la carta",
  "One line is enough. A set name, an edition, whatever the card says.":
    "Con una línea basta: un nombre de colección, una edición, lo que ponga la carta.",
  "Thank you — we will add that set.": "Gracias, añadiremos esa colección.",
  "Set and edition": "Colección y edición",
  // Shown where a catalogue product carries no expansion name at all — a real
  // state for 927 products, and better said than left blank.
  "Not named": "Sin nombre",
  "Whatever is printed on the card is enough.": "Con lo que ponga en la carta basta.",
  // The set chips that stood here asked "This card appears in" and answered with
  // expansion names, which cannot separate two alternate arts printed in the same
  // set — so the picker that replaced them asks which one the seller HAS.
  "Which printing is yours?": "¿Cuál de estas tienes?",
  "Pick the one that looks like the card in your hand. It sets the picture, the set line and the printing a buyer's search matches.":
    "Elige la que se parece a la carta que tienes en la mano. Fija la imagen, la línea de colección y la impresión que encuentra quien la busca.",
  "Set not named": "Colección sin nombre",
  // The printings of a set we hold no way of telling apart. Not a set name —
  // it is the honest label for "the rest of this one".
  "Others": "Otras",
  // When two rows say the same set, the same rarity and show the same picture,
  // and the only thing separating them is 22,93 € against 0,57 €. Both are real
  // Cardmarket products; what we cannot say is which is which, so we say that
  // instead of letting the price stand in as the difference.
  "Some of these are the same set twice. Cardmarket sells them as separate cards but does not say what differs, so the price beside each is only a reference.":
    "Algunas son la misma colección repetida. Cardmarket las vende como cartas distintas pero no dice en qué se diferencian, así que el precio de al lado es solo una referencia.",
  "Showing {shown} of {total} printings.": "Mostrando {shown} de {total} impresiones.",
  /*
   * A SECOND WORD, AND ONLY ONE, because there is now only one question.
   *
   * The screen used to spend two: "¿Cuál de las versiones tienes?" over the
   * products of a set and "¿Cuál es tu ilustración?" over the drawings. The
   * owner read them as the same question asked twice, and they were — so the
   * second is gone and this one covers both. "impresión" stays for the
   * Cardmarket printing chosen above it; "versión" is what Cardmarket
   * themselves call a copy inside a set, and it is the word a seller will read
   * on their page when they go and check.
   *
   * The labels themselves are NOT translated. "V.1" is Cardmarket's marker, not
   * a sentence, and it has to read the same here as it does there.
   */
  "Which version is yours?": "¿Cuál de las versiones tienes?",
  /*
   * THREE SENTENCES FOR ONE SLOT, and which one appears is a claim about what is
   * on the tiles. The first two are Cardmarket versions — their own harvested
   * marker, which can be checked against their page, and our count of the
   * products in the set, which is an inference and says so rather than letting a
   * number look like a fact. The third is for a set Cardmarket sells as one
   * product that the publisher drew more than once: there is no version number
   * there to report or to count, only drawings.
   */
  "This set holds more than one version of this card. These are Cardmarket's own version numbers, so you can check them against their page.":
    "Esta colección tiene más de una versión de esta carta. Estos son los números de versión de Cardmarket, así que puedes comprobarlos en su página.",
  /*
   * The third source, and the reason it is not folded into the one above: our
   * catalogue's name for a printing is a real name, but it is not the word on
   * Cardmarket's page — so this sentence must not send a seller there to check
   * it. It is what three of the five games get, having no version numbers of
   * their own for anybody to report.
   */
  "This set holds more than one version of this card. These are the names our catalogue gives them; Cardmarket may word them differently on their page.":
    "Esta colección tiene más de una versión de esta carta. Estos son los nombres que les da nuestro catálogo; puede que Cardmarket los llame de otra forma en su página.",
  "This set holds more than one version of this card. Nobody has told us which is which, so they are numbered in the order the catalogue lists them.":
    "Esta colección tiene más de una versión de esta carta. Nadie nos ha dicho cuál es cuál, así que van numeradas en el orden en que las lista el catálogo.",
  // "y el anuncio se queda sin imagen" and NOT "se queda la imagen del
  // catálogo", which is what this said before. The catalogue keeps one picture
  // per card CODE, so all ten products of ST15-002 carry the alternate art that
  // came with OP-10 — skipping the question no longer leaves a picture standing,
  // it leaves a frame that says there is none. The sentence has to promise what
  // actually happens.
  "The publisher draws this card more than once. Pick the one you are holding, or skip it and the listing shows no picture.":
    "La editora dibuja esta carta más de una vez. Elige la que tienes en la mano, o sáltalo y el anuncio se queda sin imagen.",
  // The reveal on the version grid, which stays tiles at any count — a drawing
  // cannot be chosen from a dropdown of its own name. Says the number, because
  // "ver más" beside twelve tiles does not tell anybody whether there are two
  // behind it or forty.
  "Show all {count} printings": "Ver las {count} impresiones",
  // Beside a suggestion in the card typeahead (autocomplete.jsx).
  "{count} printings": "{count} impresiones",
  "Show all {count} versions": "Ver las {count} versiones",
  // Why the list is shorter than the card's four drawings: the printing chosen
  // above was matched against the series the publisher printed each one in. Said
  // out loud, because two tiles where there are four otherwise reads as data we
  // are missing. "impresas en" and not "de": the set is where it was PRINTED.
  "Only the ones printed in {set}.": "Solo las impresas en {set}.",
  // And when changing the set takes away an answer already given. It is cleared
  // rather than kept hidden, and the sentence is what makes the clearing visible
  // to somebody who has already scrolled past the picker.
  "The version you picked is not in this set, so it is no longer chosen.":
    "La versión que elegiste no está en esta colección, así que ya no está seleccionada.",
  // The caption on a tile whose drawing the publisher gives no series name for,
  // when there is not even a card code to fall back on. A word, not a sentence.
  "Version": "Versión",
  "Condition": "Estado",
  "Notes for traders": "Notas para quien la quiera",
  "Picture": "Imagen",
  "Pick a card above and its picture is filled in for you.":
    "Elige una carta arriba y ponemos su imagen.",
  "Meeting": "Quedada",
  "Hand-off only": "Solo en mano",
  "No posting, no couriers. This is how Cardboardmarket works.":
    "Sin envíos ni mensajeros. Cardboardmarket funciona así.",

  // --- you ------------------------------------------------------------------
  /*
   * The two rights the privacy policy promises by name, and the words it uses
   * for them. "Borrar tu cuenta" and "Exportar" are what the policy says a
   * trader will find in their profile, so that is what the profile says back —
   * a person checking whether the promise is kept should not have to translate
   * between the two documents.
   */
  "Your data": "Tus datos",
  "Take a copy of everything we hold about you, or close your account for good.":
    "Llévate una copia de todo lo que guardamos sobre ti, o cierra tu cuenta para siempre.",
  "Export my data": "Exportar mis datos",
  "Putting it together…": "Preparándolo…",
  "We could not put your data together. Try again in a moment.":
    "No hemos podido preparar tus datos. Inténtalo otra vez en un momento.",
  "Delete my account": "Borrar mi cuenta",
  "Delete your account?": "¿Borrar tu cuenta?",
  "This cannot be undone.": "Esto no se puede deshacer.",
  "Keep my account": "Conservar mi cuenta",
  "Yes, delete it": "Sí, bórrala",
  "Deleting…": "Borrando…",
  "We could not delete your account. Try again in a moment.":
    "No hemos podido borrar tu cuenta. Inténtalo otra vez en un momento.",
  "Your email, your password, your point on the map, your cards, your wants and your messages are deleted. Photographs go with them, including the ones you took scanning a card. Open trades are cancelled.":
    "Se borran tu correo, tu contraseña, tu punto en el mapa, tus cartas, tus búsquedas y tus mensajes. Las fotos se van con ellos, incluidas las que hiciste al escanear una carta. Los intercambios abiertos se cancelan.",
  "Trades you already completed are kept without your name on them: they are the record of an agreement, and the other person is entitled to their own copy.":
    "Los intercambios que ya cerraste se conservan sin tu nombre: son el registro de un acuerdo, y la otra persona tiene derecho a su propia copia.",
  "Selling": "En venta",
  "Wants": "Búsquedas",
  "Settings": "Ajustes",
  "Save changes": "Guardar cambios",
  "Replay the setup": "Repetir la configuración",
  "Default radius": "Radio por defecto",
  "Your location": "Tu ubicación",
  "Alert me about want matches": "Avísame de coincidencias",
  "One notification when a want appears inside your radius":
    "Un aviso cuando una búsqueda aparezca en tu radio",
  "Sign out": "Cerrar sesión",

  // --- editing your own listing, including how many copies ------------------
  // "Comprometidas" is the word throughout for copies an accepted trade is
  // holding: somebody has agreed to come and collect them, which is stronger
  // than "reservadas" and is what the seller has to weigh before cancelling.
  "Edit listing": "Editar anuncio",
  "Notes and how many copies you have. The price and the condition cannot change — take it down and post again for those.":
    "Las notas y cuántas copias tienes. El precio y el estado no se pueden cambiar — para eso, retíralo y publícalo de nuevo.",
  "Copies you have": "Copias que tienes",
  "{committed} promised to trades you accepted, {available} still free.":
    "{committed} comprometidas en cambios que aceptaste, {available} siguen libres.",
  "None are promised yet.": "Ninguna está comprometida todavía.",
  "You cannot go below {committed} while that many copies are promised to accepted trades. Cancel one of those trades first and the copies come back.":
    "No puedes bajar de {committed} mientras esas copias estén comprometidas en cambios aceptados. Cancela uno de esos cambios y las copias vuelven.",
  "Not changed. {committed} copies are promised across {count} accepted trades, so you cannot go down to {requested}.":
    "Sin cambios. Hay {committed} copias comprometidas en {count} cambios aceptados, así que no puedes bajar a {requested}.",
  "{n} copies": "{n} copias",
  "Go to these trades": "Ir a esos cambios",
  "{available} of {quantity} free · {committed} promised":
    "{available} de {quantity} libres · {committed} comprometidas",
  /* Singular, and shown rather than omitted: a card with one copy keeps its
     line so the grid stays in step, and "1 copia" answers a real question for
     somebody who wanted three. */
  "One copy": "1 copia",
  "{quantity} copies": "{quantity} copias",
  "Copies updated.": "Copias actualizadas.",
  "Listing updated.": "Anuncio actualizado.",

  // --- onboarding, account --------------------------------------------------
  "Welcome back": "Hola de nuevo",
  "Make an account": "Crea una cuenta",
  "Sign in and pick up where you left off.": "Inicia sesión y sigue donde lo dejaste.",
  "You need one to list cards and message traders. Browsing works without.":
    "La necesitas para publicar cartas y escribir a otras personas. Para mirar, no.",
  "Your name": "Tu nombre",
  "What traders will see": "Lo que verán los demás",
  "Email": "Correo",
  "Password": "Contraseña",
  "At least 12 characters. Length beats punctuation.":
    "Doce caracteres o más. La longitud importa más que los símbolos.",
  "No account yet? Make one.": "¿No tienes cuenta? Crea una.",
  "Already have an account? Sign in.": "¿Ya tienes cuenta? Inicia sesión.",
  "Create account": "Crear cuenta",
  "Keep browsing": "Seguir mirando",
  "Continue": "Continuar",
  "Start browsing": "Empezar a mirar",
  "What do you collect?": "¿Qué coleccionas?",
  // Onboarding's game step. "Y puedes cambiarlos luego" is not padding: the step
  // no longer requires an answer, so the copy must stop sounding like one is
  // owed. Same register as the language hint a few chips below it.
  "Pick everything you buy, sell or trade. It sets your default filters, and you can change them later.":
    "Marca todo lo que compras, vendes o cambias. Define tus filtros por defecto y puedes cambiarlos luego.",
  "Add": "Añadir",
  "How far will you go?": "¿Hasta dónde te mueves?",
  "You can change this any time.": "Puedes cambiarlo cuando quieras.",
  "We show listings you could walk or ride to. Other traders only ever see how far away you are, never where.":
    "Te enseñamos anuncios a los que puedas ir andando o en bici. Los demás solo ven a qué distancia estás, nunca dónde.",
  "Use my location": "Usar mi ubicación",
  "Use my location again": "Volver a usar mi ubicación",
  "Locating…": "Localizando…",
  "Drop a pin instead": "Marcar un punto en el mapa",
  "Tap the map to place your pin.": "Toca el mapa para poner tu punto.",
  "Tap or drag the pin to adjust.": "Toca o arrastra el punto para ajustarlo.",

  // --- verification ---------------------------------------------------------
  "Confirming your email…": "Confirmando tu correo…",
  "Email confirmed": "Correo confirmado",
  "That is everything. You can post cards and message traders now.":
    "Ya está. Ya puedes publicar cartas y escribir a otras personas.",
  "That link has expired": "Ese enlace ha caducado",
  "That link is incomplete": "Ese enlace está incompleto",
  "Confirmation links are single-use and last a few hours. Ask for a fresh one.":
    "Los enlaces de confirmación son de un solo uso y duran unas horas. Pide uno nuevo.",
  "The confirmation link seems to have been cut short. Ask for a new one and use the whole thing.":
    "Parece que el enlace se ha cortado. Pide otro y úsalo entero.",
  "Send me a new link": "Mándame otro enlace",
  "Sign in to get a new link": "Inicia sesión para pedir otro",
  "Confirm your email to post cards. Check your inbox — and your spam folder.":
    "Confirma tu correo para publicar cartas. Mira tu bandeja de entrada y el spam.",
  "Send it again": "Enviar otra vez",
  "Sent": "Enviado",

  // Placeholders, hints and confirmations — the sentences nobody notices until
  // they are the only English left on a Spanish screen.
  "Proximity instead of postage. Find the card, meet the holder, hand it over.": "Cercanía en lugar de envíos. Encuentra la carta, queda con quien la tiene y cambiadla en mano.",
  "Meet in public, check the card before you pay, mark the trade done together.": "Quedad en un sitio público, revisa la carta antes de pagar y dad el cambio por hecho entre los dos.",
  "Sort by": "Ordenar por",
  "Can’t find it?": "¿No la encuentras?",
  "Add it to your wants and we’ll alert you when it appears within your radius.": "Añádela a tu lista de búsqueda y te avisamos en cuanto aparezca dentro de tu radio.",
  "We show listings you could walk or ride to. Your exact point is never shown to another trader — they only ever see a distance.": "Mostramos anuncios a los que puedes llegar andando o en transporte. Tu punto exacto nunca se le enseña a nadie: solo ven una distancia.",
  "Median meetup distance here is 1.6 km.": "Aquí la distancia mediana de un intercambio son 1,6 km.",
  "Set your radius": "Ajusta tu radio",
  "View profile": "Ver perfil",
  "Share listing": "Compartir anuncio",
  "Photos are the trader’s own. Inspect the card at the meetup before you pay.": "Las fotos son de quien publica. Revisa la carta en el encuentro antes de pagar.",
  "{radius} is as wide as the radius goes. Post a want and we'll tell you the moment something appears inside it.":
    "{radius} es el radio más amplio. Publica una búsqueda y te avisamos en cuanto aparezca algo dentro.",
  "Set where you trade from and what you collect. We match you with holders a few streets away and you agree a place to meet.": "Di desde dónde cambias y qué coleccionas. Te cruzamos con gente a unas calles y quedáis en un sitio.",
  "Grade it as you would want it graded for you.": "Valórala como te gustaría que la valorasen para ti.",
  "Price": "Precio",
  "How do you want to move it?": "¿Cómo quieres moverla?",
  "Save as draft": "Guardar como borrador",
  "How it will look": "Cómo se verá",
  "Traders see the distance from their own radius, not your address.": "Los demás ven la distancia desde su propio radio, no tu dirección.",
  "Radius set. These are the cards within it.": "Radio ajustado. Estas son las cartas que hay dentro.",
  "Want added. We’ll ping you when it appears within your radius.": "Búsqueda añadida. Te avisamos cuando aparezca dentro de tu radio.",
  "Reported. Thank you — we read these.": "Denuncia enviada. Gracias: las leemos todas.",
  "One moment.": "Un momento.",
  "No wants yet": "Aún no buscas nada",
  "Name a card and we'll watch every listing inside your radius for it.": "Di qué carta quieres y vigilamos por ti todos los anuncios dentro de tu radio.",
  "Condition or notes": "Estado o notas",
  "EX or better": "EX o mejor",
  "Nothing listed yet": "Aún no has publicado nada",
  "Post a card and traders inside your radius see it straight away.": "Publica una carta y quien esté dentro de tu radio la ve al momento.",
  "We only alert you about listings you could reach — no shipping offers.": "Solo te avisamos de anuncios a los que puedes llegar. Nada de ofertas por envío.",
  "Settings saved.": "Ajustes guardados.",
  "Café Central": "Café Central",
  "Tables outside, quiet before noon": "Mesas fuera, tranquilo antes de mediodía",
  "Waited 40 minutes at the café.": "Esperé 40 minutos en el café.",

  // The landing hero and the footer — the first and last English anybody saw.
  "cards near you, not in the post": "cartas cerca de ti, no en el buzón",
  "median meetup": "distancia mediana",
  "shipping paid": "gastos de envío",
  "median time to trade": "tiempo mediano de cambio",
  "Step {n} of {total}": "Paso {n} de {total}",
  "Market": "Mercado",
  "You": "Tú",
  "Your trades": "Tus cambios",
  "Profile": "Perfil",
  "Legal": "Legal",
  "Safety": "Seguridad",
  "Base Set · Holo · 1999": "Base Set · Holo · 1999",
  "Sleeved since 2011. Light edge wear on the back.": "En funda desde 2011. Ligero desgaste en los bordes por detrás.",
  "2 days": "2 días",

  // Toasts. Reported in Spanish view still showing English.
  "Location and radius saved.": "Ubicación y radio guardados.",
  "Radius set to {radius}.": "Radio ajustado a {radius}.",
  "Listed. Traders within {radius} see it now.": "Publicado. Quien esté a {radius} ya lo ve.",
  "Trade proposed. We'll ping you when {name} replies.": "Cambio propuesto. Te avisamos cuando {name} responda.",
  "Trade done. Both of you confirmed.": "Cambio hecho. Lo habéis confirmado los dos.",
  "Marked done on your side. Waiting for {name}.": "Marcado como hecho por tu parte. Falta {name}.",
  "Meetup confirmed. It is in your active list.": "Encuentro confirmado. Está en tu lista de activos.",
  "Declined. Nobody was charged.": "Rechazado. Nadie ha pagado nada.",
  "Trade cancelled. Nobody was charged.": "Cambio cancelado. Nadie ha pagado nada.",
  "Sent. Check your inbox — and your spam folder.": "Enviado. Mira tu bandeja de entrada — y la carpeta de spam.",
  "We could not send it just now. Try again in a few minutes.": "No hemos podido enviarlo ahora mismo. Prueba otra vez en unos minutos.",
  // Browse's heading, one sentence per ordering — see SORTS in browse.jsx. It
  // said "closest first" under every sort, which was a claim about the grid that
  // the grid stopped honouring the moment somebody changed the Sort by control.
  "1 listing within {radius}, closest first. Nobody posts anything — you hand it over.": "1 anuncio a {radius}, empezando por lo más cerca. Aquí nadie envía nada: se entrega en mano.",
  "{count} listings within {radius}, closest first. Nobody posts anything — you hand it over.": "{count} anuncios a {radius}, empezando por lo más cerca. Aquí nadie envía nada: se entrega en mano.",
  "1 listing within {radius}, cheapest first. Nobody posts anything — you hand it over.": "1 anuncio a {radius}, empezando por lo más barato. Aquí nadie envía nada: se entrega en mano.",
  "{count} listings within {radius}, cheapest first. Nobody posts anything — you hand it over.": "{count} anuncios a {radius}, empezando por lo más barato. Aquí nadie envía nada: se entrega en mano.",
  "1 listing within {radius}, most expensive first. Nobody posts anything — you hand it over.": "1 anuncio a {radius}, empezando por lo más caro. Aquí nadie envía nada: se entrega en mano.",
  "{count} listings within {radius}, most expensive first. Nobody posts anything — you hand it over.": "{count} anuncios a {radius}, empezando por lo más caro. Aquí nadie envía nada: se entrega en mano.",
  "1 listing within {radius}, newest first. Nobody posts anything — you hand it over.": "1 anuncio a {radius}, empezando por lo más reciente. Aquí nadie envía nada: se entrega en mano.",
  "{count} listings within {radius}, newest first. Nobody posts anything — you hand it over.": "{count} anuncios a {radius}, empezando por lo más reciente. Aquí nadie envía nada: se entrega en mano.",

  // Found by app/scan-untranslated.mjs, which parses rather than greps — every
  // one of these was invisible to a regex: split around inline markup, built by
  // concatenation, or written with an HTML entity.
  "1 of your wants is within {radius}": "1 de tus búsquedas está a {radius}",
  "{count} of your wants are within {radius}": "{count} de tus búsquedas están a {radius}",
  "Show 1 listing": "Ver 1 anuncio",
  "Show {count} listings": "Ver {count} anuncios",
  "Optional. Suggest a place if you have one in mind — otherwise {name} picks when they accept.": "Opcional. Propón un sitio si tienes uno en mente; si no, lo elige {name} al aceptar.",
  // The empty picture frame on a listing card. It says the PICTURE is missing,
  // not that the card is: the frame appears both when nobody photographed the
  // card and when we cannot say which drawing it is, and either way what is
  // absent is an image. Short because it is centred in a 3:4 tile sixty at a
  // time; the listing page says it at length, where there is room.
  "No picture": "Sin imagen",
  "back": "reverso",
  "corners": "esquinas",
  "holo": "holo",
  "Other traders have reported problems with this account. Nothing here says you should not trade — meet somewhere public, check the card carefully, and take your time.": "Otras personas han denunciado problemas con esta cuenta. Esto no dice que no debas cambiar: quedad en un sitio público, revisa bien la carta y tómate tu tiempo.",
  "More from {name}": "Más de {name}",
  "No location from your browser — drop a pin on the map instead.": "El navegador no ha dado tu ubicación. Marca un punto en el mapa.",
  "The ring is your radius. Your exact point is never shown to anyone, and neither is theirs — what you see is a distance.": "El círculo es tu radio. Tu punto exacto no se le enseña a nadie, ni el suyo a ti: solo veis una distancia.",
  // Split from the note below rather than one flowing sentence. The clause that
  // counts is the only part that differs between waiting and answered, and on
  // its own line it is one line at every width — so the card does not change
  // height when the count arrives. Spanish is where that showed up.
  /*
   * What a distance under the floor reads as, everywhere one is shown.
   *
   * A band and not a figure, because a sub-kilometre distance locates a person
   * precisely enough to matter — the floor is a privacy rule, not a rounding
   * preference. `DistanceTag` has taken a `floorLabel` since it was written and
   * nobody ever passed one, so the nearest listings on browse, on the listing
   * page and on nearby all said "less than 1 km" in English: the cards a Spanish
   * reader is most likely to act on were the ones speaking to them in English.
   */
  "less than 1 km": "menos de 1 km",
  // The second step of a trade proposal, under "Where and when?". The distance
  // inside it comes from fmtKm and lands on {distance} already worded.
  "{name} is {distance} from you.": "{name} está a {distance} de ti.",
  "It goes live to traders within {radius}. No listing fee, no shipping label.": "Se publica para quien esté a {radius}. Sin comisión por publicar y sin etiqueta de envío.",
  "The publisher’s picture of this card, so you do not have to photograph it. It shows the card, not YOUR card — describe the wear in the notes above, and both of you check it in person before anything changes hands.": "La foto del fabricante, para que no tengas que hacerla tú. Enseña la carta, no TU carta: describe el desgaste en las notas de arriba y revisadla en persona antes de cambiar nada.",
  "Report {name}": "Denunciar a {name}",
  "Waiting for {name}": "Falta {name}",
  "Used to work out distances. Never shown to another trader — they see a distance and nothing else.": "Se usa para calcular distancias. Nunca se le enseña a nadie: solo ven una distancia y nada más.",
  "YOU": "TÚ",
  "trading within {radius}": "cambia a {radius}",
  "Trades done": "Cambios hechos",
  "Listings live": "Anuncios activos",
  "Meetups agreed": "Quedadas acordadas",
  "Set where you are and this becomes a list of cards you could go and collect. Until then it is everything on the site.": "Marca dónde estás y esto pasa a ser una lista de cartas que puedes ir a recoger. Hasta entonces es todo lo que hay.",
  "Anyone close enough to come and get it will see it. No listing fee, no shipping label.": "Lo verá quien esté lo bastante cerca como para venir a por ella. Sin comisión por publicar y sin etiqueta de envío.",
  "Manage your listings": "Gestiona tus anuncios",
  "Link copied.": "Enlace copiado.",
  "Could not share that. The address bar has the link.": "No se ha podido compartir. El enlace está en la barra de direcciones.",
  "Could not save that. Your location may not be up to date.": "No se ha podido guardar. Puede que tu ubicación no esté actualizada.",
  "Reported": "Denunciado",
  "The listing was withdrawn.": "El anuncio se retiró.",
  "Report and cancel": "Denunciar y cancelar",
  "Watching for {card}. We’ll tell you when one appears inside your radius.": "Vigilando {card}. Te avisamos cuando aparezca una dentro de tu radio.",
  "Watch for “{card}”": "Vigilar «{card}»",
  "{count} nearby": "{count} cerca",
  "Take it down": "Quitarlo",
  // SEVERAL LISTINGS AT ONCE. "Quitar" here where a want says "Eliminar": the
  // two acts differ and so do the words. A listing is withdrawn — it keeps its
  // row, its photographs and its conversations — which is what "Quitarlo" has
  // always said on the single card's own button.
  //
  // The tick box over a card carries the card's name for a screen reader and
  // nothing visible: drawn, it would print the title a second time on a screen
  // that is already a grid of titles.
  "Select {card}": "Seleccionar {card}",
  "Take down {n}": "Quitar {n}",
  "Take down {n} listings?": "¿Quitar {n} anuncios?",
  "They stop showing to other traders. Any conversation about them stays where it is.": "Dejan de mostrarse a los demás. Las conversaciones sobre ellos se quedan donde están.",
  "Take them down": "Quitarlos",
  "{n} listings taken down. Nobody sees them now.": "{n} anuncios retirados. Ya no los ve nadie.",
  "{n} of the {asked} you chose are down. The rest were already off the market.": "Se han retirado {n} de los {asked} que elegiste. Los demás ya no estaban en el mercado.",
  "Take down “{card}”?": "¿Quitar «{card}»?",
  "It stops showing to other traders. Any conversation about it stays where it is.": "Deja de mostrarse a los demás. Las conversaciones sobre él se quedan donde están.",
  "Listing taken down. Nobody sees it now.": "Anuncio retirado. Ya no lo ve nadie.",
  "No picture for this one": "Sin foto para esta",
  "The publisher’s picture. Check the actual card at the meetup before you pay.": "La foto del fabricante. Revisa la carta de verdad en el encuentro antes de pagar.",
  "Check the card at the meetup before you pay.": "Revisa la carta en el encuentro antes de pagar.",
  "Saved. Distances are measured from the pin above.": "Guardado. Las distancias se miden desde el punto de arriba.",
  "Nothing saved yet, so nothing can be measured from you.": "Aún no hay nada guardado, así que no se puede medir ninguna distancia.",
  "Edit notes": "Editar notas",
  "Notes updated.": "Notas actualizadas.",
  "Describe the wear, the sleeve, anything a buyer should know. The price and condition cannot change — take it down and post again for those.": "Describe el desgaste, la funda, lo que quien compre deba saber. El precio y el estado no se pueden cambiar: para eso, quítalo y publícalo otra vez.",
  "Watch for this card?": "¿Vigilar esta carta?",
  "We’ll tell you when one appears inside your radius. You can change the game or add a note first.": "Te avisamos cuando aparezca una dentro de tu radio. Antes puedes cambiar el juego o añadir una nota.",
  "Sound when something arrives": "Sonido cuando llega algo",
  "A short tone when a message or trade appears while this tab is in the background": "Un tono corto cuando llega un mensaje o un cambio con esta pestaña en segundo plano",
  // "ELIMINAR", NOT "QUITAR", chosen by the owner on sight — and it is the more
  // truthful of the two. A want really is deleted: the row goes, where a listing
  // taken down is only withdrawn and keeps its "Quitarlo". The words now differ
  // because the acts do, which is worth more than the two reading alike.
  //
  // The whole family moves together, the single row's × included. One of them
  // saying "Quitar Nami" beside a button saying "Eliminar 2" would be two words
  // for one act, on one screen.
  //
  // Feminine throughout, because the thing counted is "búsquedas".
  "Remove {card}": "Eliminar {card}",
  //
  // No string names the tick box: the row's own title does that, inside the
  // label. A "Seleccionar {card}" beside it would put every card's name in the
  // document twice.
  // SHORT ENOUGH FOR ONE ROW ON A PHONE, which is the whole reason for the
  // wording. "Seleccionar las 6 de la lista" and "Quitar las 2 seleccionadas"
  // wrapped the bar onto two rows, and the bar's space is reserved whether or
  // not anything is ticked -- so every pixel of it is paid for on every visit
  // to the tab, selection or no selection.
  //
  // "Ninguna" against "Seleccionar {n}" reads as the pair it is: all of them,
  // none of them. The English "None" does the same work beside "Select all".
  "Select all {n}": "Seleccionar {n}",
  "None": "Ninguna",
  "Remove {n}": "Eliminar {n}",
  "{n} wants removed.": "{n} búsquedas eliminadas.",
  "{n} of the {asked} you chose are gone. The rest had already been removed.": "Se han eliminado {n} de las {asked} que elegiste. Las demás ya no estaban.",
  "We could not load the listings": "No hemos podido cargar los anuncios",
  "Something went wrong at our end, not yours. Your radius and filters are untouched.": "Ha fallado algo por nuestra parte, no por la tuya. Tu radio y tus filtros siguen igual.",
  "Try again": "Volver a probar",
  "Finding what is within your radius.": "Buscando lo que hay dentro de tu radio.",
  "Loading your listings.": "Cargando tus anuncios.",
  "We could not load your listings": "No hemos podido cargar tus anuncios",
  "Something went wrong at our end. They are still there.": "Ha fallado algo por nuestra parte. Siguen ahí.",
  "Loading your conversations.": "Cargando tus conversaciones.",
  "We could not load your conversations": "No hemos podido cargar tus conversaciones",
  "Something went wrong at our end, not yours. Nothing has been lost.": "Ha fallado algo por nuestra parte, no por la tuya. No se ha perdido nada.",
  "Loading your trades.": "Cargando tus intercambios.",
  "We could not load your trades": "No hemos podido cargar tus intercambios",
  "Something went wrong at our end, not yours. Nothing has been agreed or cancelled.": "Ha fallado algo por nuestra parte, no por la tuya. No se ha acordado ni cancelado nada.",
  "Loading your wants.": "Cargando tus búsquedas.",
  "We could not load your wants": "No hemos podido cargar tus búsquedas",
  "Something went wrong at our end, not yours. They are still being watched for.": "Ha fallado algo por nuestra parte, no por la tuya. Las seguimos vigilando.",
  "Worst condition you would take": "Peor estado que aceptarías",
  "Any condition": "Cualquier estado",
  "Mint": "Mint",
  "Near mint": "Near mint",
  "Excellent": "Excelente",
  "Good": "Bueno",
  "Light play": "Poco jugada",
  "Played": "Jugada",
  "Poor": "Mala",

  // The language a CARD is printed in, not the one the interface is in — see
  // migration 049. Sentence case, so the first letter is capital and the rest is
  // not: Spanish does not capitalise the name of a language, and each of these is
  // a whole label rather than a word inside a sentence.
  "English": "Inglés",
  "French": "Francés",
  "German": "Alemán",
  "Spanish": "Español",
  "Italian": "Italiano",
  "Simplified Chinese": "Chino simplificado",
  "Japanese": "Japonés",
  "Portuguese": "Portugués",
  "Russian": "Ruso",
  "Korean": "Coreano",
  "Traditional Chinese": "Chino tradicional",
  // Six more from migration 053, same sentence case as the eleven above.
  "Dutch": "Neerlandés",
  "Polish": "Polaco",
  "Czech": "Checo",
  "Hungarian": "Húngaro",
  "Indonesian": "Indonesio",
  "Thai": "Tailandés",
  // The prompt at the top of the card-language picker. Not an answer and not a
  // default: it is what the control says while nobody has picked, and it cannot
  // be posted. "Elige un idioma" rather than "Selecciona": the form speaks to
  // the person in the same register as every other hint on it.
  "Choose a language": "Elige un idioma",
  // Browse's language filter, and its twin in the profile.
  //
  // "Idioma de la carta" rather than a bare "Idioma": the profile already has an
  // "Idioma" row and it switches the INTERFACE. Two controls a few lines apart,
  // both labelled "Idioma", doing entirely different things, is how somebody
  // changes the site to Japanese trying to filter for Japanese cards.
  "Card language": "Idioma de la carta",
  // Onboarding's own step, once the languages stopped sharing one with the games.
  // "De carta" and not "de la carta": the step asks about a kind of language, not
  // about one particular card — and it has to stay clear of the interface
  // language the switch in the corner of the same screen controls.
  "Which card languages?": "¿Qué idiomas de carta?",
  "Card languages you want to see": "Idiomas de carta que quieres ver",
  /*
   * What an EMPTY selection looks like, which is what every account holds until
   * somebody answers. Not a language and not a stored value — "todos" cannot be
   * printed on a card, and the enum behind `card.language` has no such member.
   *
   * "Todos los idiomas", masculine plural, agreeing with "idiomas" rather than
   * with "cartas": the chip sits in a row of language names and is one of them
   * grammatically, whatever it means.
   */
  "All languages": "Todos los idiomas",
  "Browse opens on these. You can still see any language from the filters.":
    "Explorar se abre con estos. Puedes ver cualquier idioma desde los filtros.",
  "All languages, so browse opens on everything.":
    "Todos los idiomas, así que Explorar se abre con todo.",
  "Those languages could not be saved.": "No se han podido guardar esos idiomas.",
  // Onboarding, under the language chips. It has to say out loud that skipping
  // this is allowed: a step that looks like a question people must answer is one
  // they will answer at random to get past it, and a random preference is worse
  // than none. "Si te da igual" is the register the rest of the flow uses.
  "Leave it on all languages if you do not mind. You can change it later.":
    "Déjalo en todos los idiomas si te da igual. Puedes cambiarlo luego.",
  // Foil. The word is the same in Spanish — it is what the game says on the
  // card and what every player in a shop says out loud; "brillante" would be a
  // translation nobody uses and half the readers would not recognise.
  "Foil": "Foil",
  "A foil, holo or shiny printing of this card.": "Una edición foil, holo o brillante de esta carta.",
  "The language printed on the card, not the one you are reading.":
    "El idioma impreso en la carta, no el que estás leyendo.",
  // "La edición" rather than "la impresión": Cardmarket sells the Japanese print
  // run as its own set, and "la edición japonesa" is how a Spanish-speaking
  // player refers to it. "Impresión japonesa" reads as a printing process.
  //
  // "LA EDICIÓN EN {language}" AND NOT "LA EDICIÓN {language}", which is the
  // whole reason this sentence is shaped the way it is. The name arrives from
  // CARD_LANGUAGE_NAMES as the dropdown shows it — "Japonés", "Coreano" — which
  // are masculine nouns, and "edición" is feminine: dropping one straight into
  // the sentence yields "la edición Japonés", wrong in both gender and case.
  // After "en" the same word is a noun again and agrees with nothing, so one
  // sentence serves every language the suffix list can produce.
  "Filled in from the set you picked, which is the {language} printing. Change it if your copy is not.":
    "Lo hemos puesto por la expansión que has elegido, que es la edición en {language}. Cámbialo si tu copia no lo es.",
  "Only cards on your wants list": "Solo cartas de tu lista de búsqueda",
  // The trader's own photograph, as opposed to the publisher's catalogue image.
  // "Fotografiadas por quien las publica" rather than "con foto", because every
  // listing has a picture and the distinction is whose.
  "Only cards the trader has photographed": "Solo cartas fotografiadas por quien las publica",
  "Hides listings that show only the catalogue picture": "Oculta los anuncios que solo muestran la imagen del catálogo",
  "Show everything": "Ver todo",
  "{count} trades": "{count} cambios",
  "new here": "nuevo aquí",

  // --- importing a Cardmarket inventory --------------------------------------
  // The three words that must not blur into each other: importada (became a
  // listing), ya estaba (was already here, untouched), no se ha podido leer
  // (not listed, something to fix). Spanish has the same trap English does — a
  // single "con problemas" covering the last two — so it is avoided here in the
  // same way.
  // THE SHOP'S MODE. "Sincronizar" is not offered as a word on its own: the two
  // options are sentences about the FILE, because the question a person can
  // answer is what they just exported, not what our importer should do with it.
  "What this file is": "Qué es este fichero",
  "New cards to add to what I have": "Cartas nuevas para añadir a lo que ya tengo",
  "My whole inventory — keep it in step": "Todo mi inventario: mantenlo igual aquí",
  "Will be rewritten": "Se actualizarán",
  "Rewritten": "Actualizadas",
  "Will come down": "Se retirarán",
  "Came down": "Retiradas",
  "Import inventory": "Importar inventario",
  "Made with a free browser extension that is not ours.": "Se genera con una extensión de navegador gratuita que no es nuestra.",
  "More information": "Más información",
  "Getting the file": "Cómo conseguir el archivo",
  "The export comes from a free browser extension. It is not ours.": "La exportación la genera una extensión de navegador gratuita. No es nuestra.",
  "Install Cardmarket Stock Exporter from the Chrome Web Store, open your stock page on Cardmarket, and export. The file lands in your downloads.": "Instala Cardmarket Stock Exporter desde la Chrome Web Store, abre tu página de stock en Cardmarket y exporta. El archivo se guarda en tus descargas.",
  "Install Cardmarket Stock Exporter from the Chrome Web Store, open your want lists on Cardmarket, and export them from the extension's Wants tab. The file lands in your downloads.": "Instala Cardmarket Stock Exporter desde la Chrome Web Store, abre tus listas de wants en Cardmarket y expórtalas desde la pestaña Wants de la extensión. El archivo se guarda en tus descargas.",
  "Use version 2.4.3 or later.": "Usa la versión 2.4.3 o posterior.",
  "Versions before 2.2.13 leave the language blank for some cards, and can write the wrong language back to your Cardmarket stock.": "Las versiones anteriores a la 2.2.13 dejan el idioma en blanco en algunas cartas, y pueden escribir el idioma equivocado en tu stock de Cardmarket.",
  "Older ones can leave the cards out of a want list, keep only one language per want, and lose its foil setting.": "Las anteriores pueden dejar fuera las cartas de una lista de wants, quedarse con un solo idioma por want y perder su ajuste de foil.",
  "We did not write it, we do not maintain it, and we cannot support it. It is open source, and it says it sends nothing anywhere: it reads your stock page and saves a file to your computer.": "No la hemos escrito nosotros, no la mantenemos y no podemos darte soporte con ella. Es de código abierto y afirma que no envía nada a ningún sitio: lee tu página de stock y guarda un archivo en tu ordenador.",
  "We did not write it, we do not maintain it, and we cannot support it. It is open source, and it says it sends nothing anywhere: it reads your want lists and saves a file to your computer.": "No la hemos escrito nosotros, no la mantenemos y no podemos darte soporte con ella. Es de código abierto y afirma que no envía nada a ningún sitio: lee tus listas de wants y guarda un archivo en tu ordenador.",
  "We never see your Cardmarket account. You export a file and choose it here — no login of yours comes near us.": "Nunca vemos tu cuenta de Cardmarket. Exportas un archivo y lo eliges aquí; ninguna credencial tuya pasa por nosotros.",
  "Open the Chrome Web Store listing": "Abrir la ficha en la Chrome Web Store",
  "Got it": "Entendido",
  "Read the full guide": "Leer la guía completa",
  "Source": "Origen",
  "File": "Archivo",
  // The step between the file and the result. "Revisión" rather than "Vista
  // previa": what is on that step is not a picture of the import, it is the
  // import's own answer, and the trader is being asked to look at it.
  "Review": "Revisión",
  "Result": "Resultado",
  "Import your inventory": "Importa tu inventario",
  "Import your wants": "Importa tus wants",
  "Import your wants list": "Importa tu lista de búsquedas",
  "See your wants": "Ver tus búsquedas",
  "A list you already keep somewhere else becomes listings here. Prices come across once and are never updated on their own.": "Una lista que ya llevas en otro sitio se convierte en anuncios aquí. Los precios se copian una vez y no se actualizan solos.",
  "How do you want to bring it in?": "¿Cómo quieres traerlo?",
  "Cardmarket Stock Exporter CSV": "CSV de Cardmarket Stock Exporter",
  "The file the extension generates. Quantity, condition, language and price come across.": "El archivo que genera la extensión. Cantidad, estado, idioma y precio se copian.",
  "It is the only one we read today. Tell us what else you use and we will add it.": "Es la única que leemos hoy. Dinos qué más usas y la añadimos.",
  "Your stock over there becomes listings here. Prices come across once and are never updated on their own.":
    "Tu stock de allí pasa a ser anuncios aquí. Los precios se traen una vez y no se actualizan solos.",
  "Drop your stock export here": "Suelta aquí tu exportación de stock",
  "Choose a file": "Elegir un archivo",
  "Choose a different file": "Elegir otro archivo",
  "Choose your export and we will show you what it holds before anything is imported.":
    "Elige tu exportación y te enseñamos lo que lleva antes de importar nada.",
  "Reading your file…": "Leyendo tu archivo…",
  "{mb} MB. A file this size takes a few seconds — stay on this page.":
    "{mb} MB. Un archivo así tarda unos segundos — quédate en esta página.",
  "This takes a moment.": "Tarda un momento.",
  "That file is {mb} MB. The largest we can take is {max} MB.":
    "Ese archivo son {mb} MB. Lo más grande que podemos leer son {max} MB.",
  "Import": "Importar",
  "Importing…": "Importando…",
  "What the file looks like": "Cómo es el archivo",
  "Your stock page on Cardmarket, exported as a semicolon-separated CSV. Whatever else it carries, it has to carry these:":
    "Tu página de stock en Cardmarket, exportada como CSV separado por punto y coma. Lleve lo que lleve, tiene que llevar estas:",
  "Your wants are a different export with different columns. If that is the file that arrives, we say so instead of failing every row of it.":
    "Tus listas de wants son otra exportación, con otras columnas. Si llega ese archivo, te lo decimos en vez de fallar fila por fila.",
  // The same box on the wants step: the wants file, and the stock as the other one.
  "Your want lists on Cardmarket, exported as a semicolon-separated CSV. Whatever else it carries, it has to carry these:":
    "Tus listas de wants en Cardmarket, exportadas como CSV separado por punto y coma. Lleve lo que lleve, tiene que llevar estas:",
  "Your stock is a different export with different columns. If that is the file that arrives, we say so instead of failing every row of it.":
    "Tu stock es otra exportación, con otras columnas. Si llega ese archivo, te lo decimos en vez de fallar fila por fila.",

  // The five figures over the report. Short, because they sit under numbers.
  "Rows read": "Filas leídas",
  "Now listed": "Ya anunciadas",
  // The same figure on the review step, in the tense that step is written in.
  "Will be listed": "Se anunciarán",
  // The ones that were taken down and have gone back up. "De nuevo a la venta"
  // is the phrase, cut to what fits under a number: it says the advert is live
  // again, which is the fact, rather than "restauradas", which sounds like a
  // repair somebody performed on it.
  "Back up": "De nuevo a la venta",
  "Will go back up": "Volverán a la venta",
  "Will be saved": "Se guardarán",
  "Now saved": "Guardadas",
  "Already here": "Ya estaban",
  "Not read": "Sin leer",

  // The review step: the same report, before it is true.
  //
  // "Todavía no se ha importado nada" first and plainly. Somebody who reads only
  // the numbers has to be able to come back to one sentence that says the import
  // has not happened — this is that sentence, and it sits above them.
  "Nothing has been imported yet. This is what will happen if you go ahead.":
    "Todavía no se ha importado nada. Esto es lo que pasará si sigues adelante.",
  "One row will become a listing.": "Una fila se convertirá en un anuncio.",
  "{n} rows will become listings.": "{n} filas se convertirán en anuncios.",
  "One row is already here, and will be left exactly as it is.":
    "Una fila ya está aquí y se quedará exactamente como está.",
  "{n} rows are already here, and will be left exactly as they are.":
    "{n} filas ya están aquí y se quedarán exactamente como están.",
  "One row cannot be read, and will not be listed.":
    "Una fila no se puede leer y no se anunciará.",
  "{n} rows cannot be read, and will not be listed.":
    "{n} filas no se pueden leer y no se anunciarán.",
  "Nothing is guessed: a row we cannot read is left out rather than filled in.":
    "No suponemos nada: una fila que no podemos leer se queda fuera en vez de rellenarse.",
  "Nothing in this file is new. Going ahead will not add anything.":
    "En este archivo no hay nada nuevo. Seguir adelante no añadirá nada.",
  // The revivals, in the tense of the step they appear on.
  //
  // "Retirada" is the word the takedown button already uses, so the sentence
  // names the thing the trader did rather than describing it afresh. The price
  // and the quantity are named out loud on purpose: this is the one outcome of
  // an import that changes something that was already here, and a trader has to
  // read which of the two prices they are about to be selling at.
  "One row is here but taken down. It will go back up, with this file's price and quantity.":
    "Una fila ya está aquí, pero retirada. Volverá a la venta, con el precio y la cantidad de este archivo.",
  "{n} rows are here but taken down. They will go back up, with this file's price and quantity.":
    "{n} filas ya están aquí, pero retiradas. Volverán a la venta, con el precio y la cantidad de este archivo.",
  "One row that was taken down is back up, with this file's price and quantity.":
    "Una fila que estaba retirada vuelve a estar a la venta, con el precio y la cantidad de este archivo.",
  "{n} rows that were taken down are back up, with this file's price and quantity.":
    "{n} filas que estaban retiradas vuelven a estar a la venta, con el precio y la cantidad de este archivo.",
  // Why reviving beats creating a second advert, said once and plainly.
  "Each keeps the listing it had, so its link and any trade on it still work.":
    "Cada una conserva el anuncio que ten\u00eda, as\u00ed que su enlace y cualquier intercambio en curso siguen funcionando.",
  // The confirm names what it is about to do. "Cartas", not "filas": a row is
  // what the file has, a card is what the trader is getting.
  "Import one card": "Importar una carta",
  "Import {n} cards": "Importar {n} cartas",
  // And when the two answers disagree, the later one wins and says so.
  "That is not quite what the preview said. What you are looking at now is what actually happened.":
    "No es exactamente lo que decía la revisión. Lo que estás viendo ahora es lo que ha pasado de verdad.",

  "That file has no rows in it.": "Ese archivo no tiene ninguna fila.",
  "One row is now a listing.": "Una fila ya es un anuncio.",
  "{n} rows are now listings.": "{n} filas ya son anuncios.",
  "One row was already here, and nothing about it changed.":
    "Una fila ya estaba aquí y no ha cambiado nada en ella.",
  "{n} rows were already here, and nothing about them changed.":
    "{n} filas ya estaban aquí y no ha cambiado nada en ellas.",
  // "que siga en pie" — still standing, still up. The promise narrowed when
  // withdrawn listings started coming back: a re-import never reprices an
  // advert that is LIVE, which is what the rule was always about. One that was
  // deliberately taken down and deliberately re-uploaded is the other case, and
  // the sentence above about revivals is where that is said.
  "Importing the same file twice never doubles an inventory, and never rewrites a price on a listing that is still up.":
    "Importar el mismo archivo dos veces nunca duplica un inventario ni reescribe el precio de un anuncio que siga en pie.",
  "One row could not be read, and is not listed.":
    "Una fila no se ha podido leer y no está anunciada.",
  "{n} rows could not be read, and are not listed.":
    "{n} filas no se han podido leer y no están anunciadas.",
  "Nothing was guessed: a row we cannot read is left out rather than filled in.":
    "No hemos supuesto nada: una fila que no podemos leer se queda fuera en vez de rellenarse.",

  // The grouped reasons, as a table.
  "What happened": "Qué ha pasado",
  // The same heading on the review step, in that step's tense.
  "What will happen": "Qué va a pasar",
  "Rows": "Filas",
  "In your file": "En tu archivo",
  "Nothing to do": "Nada que hacer",
  "Not listed": "Sin anunciar",
  "Will not be listed": "No se anunciará",
  "and {n} more": "y {n} más",

  "See your listings": "Ver tus anuncios",
  "Import another file": "Importar otro archivo",
  "Tell us about this": "Cuéntanoslo",
  "That sends us the reasons and the values above, and nothing else out of your file.":
    "Eso nos envía los motivos y los valores de arriba, y nada más de tu archivo.",

  // What the whole upload can be refused for. Each carries a number or a list,
  // which is why they are sentences here rather than codes in api.jsx's table.
  "That is not a stock export — it has no {columns} column. It may be your wants file: go back and choose the wants list instead.":
    "Eso no es una exportación de stock: no tiene la columna {columns}. Puede que sea tu archivo de wants: vuelve atrás y elige la lista de wants.",
  "That is not a wants export — it has no {columns} column. It may be your stock file: go back and choose the stock export instead.":
    "Eso no es una exportación de wants: no tiene la columna {columns}. Puede que sea tu archivo de stock: vuelve atrás y elige la exportación de stock.",
  "Cardmarket wants lists CSV": "CSV de listas de wants de Cardmarket",
  /* An import too big to finish while somebody watches — migración 093/094. */
  "Working on it": "En ello",
  "That file is big enough that we are reading it in the background. It will take a few minutes.":
    "Ese archivo es lo bastante grande como para que lo leamos en segundo plano. Tardará unos minutos.",
  "You can leave this page. Come back to it and the result will be here.":
    "Puedes salir de esta página. Vuelve y el resultado estará aquí.",
  "It did not go through": "No ha salido bien",
  "That file is not a wants export. Go back and choose the stock export instead.":
    "Ese archivo no es una exportación de wants. Vuelve atrás y elige la exportación de stock.",
  "That file is not a stock export. Go back and choose the wants list instead.":
    "Ese archivo no es una exportación de stock. Vuelve atrás y elige la lista de wants.",
  "Something went wrong at our end and nothing was imported. Nothing was changed.":
    "Algo ha fallado por nuestra parte y no se ha importado nada. No se ha cambiado nada.",
  "Done": "Listo",
  "Reading your file. {n} rows so far.": "Leyendo tu archivo. {n} filas hasta ahora.",
  "Reading your file in the background.": "Leyendo tu archivo en segundo plano.",
  "Your import did not go through.": "Tu importación no ha salido adelante.",
  "Your wants are imported.": "Tus wants ya están importados.",
  "Your listings are imported.": "Tus anuncios ya están importados.",
  "See it": "Verlo",
  "See the result": "Ver el resultado",
  "A file this big goes up in pieces. Stay on this page until it is sent.": "Un archivo así de grande sube por partes. Quédate en esta página hasta que termine.",
  "Sending your file…": "Enviando tu archivo…",
  "{done} of {total} KB sent.": "{done} de {total} KB enviados.",
  "{done} of {total} MB sent.": "{done} de {total} MB enviados.",
  "about {n}": "unos {n}",
  "{n} rows read so far.": "{n} filas leídas hasta ahora.",
  "{n} of {total} rows became wants.": "{n} de {total} filas se han convertido en wants.",
  "{n} of {total} rows became listings.": "{n} de {total} filas se han convertido en anuncios.",
  "One row will become a want.": "Una fila se convertirá en un want.",
  "{n} rows will become wants.": "{n} filas se convertirán en wants.",
  "One row is now a want.": "Una fila es ahora un want.",
  "{n} rows are now wants.": "{n} filas son ahora wants.",
  "The cards you are looking for, brought over in one go. The list each one is on comes across as a note.":
    "Las cartas que buscas, traídas de una vez. La lista en la que está cada una viene como nota.",
  "The cards you are looking for. The list each one is on comes across as a note.":
    "Las cartas que buscas. La lista en la que está cada una viene como nota.",
  "That file is larger than {mb} MB.": "Ese archivo pasa de {mb} MB.",

  // Recuperar la cuenta cuando se ha perdido la contraseña.
  "Forgot your password?": "¿Has olvidado la contraseña?",
  "Tell us the email you signed up with and we will send you a link to choose a new password.":
    "Dinos el correo con el que te registraste y te mandamos un enlace para elegir una contraseña nueva.",
  "Send me a link": "Mándame un enlace",
  "I remembered it. Back to sign in.": "Ya me acuerdo. Volver a entrar.",
  "Check your email": "Mira tu correo",
  // No dice "hemos enviado": el servidor no cuenta si esa dirección tiene
  // cuenta, y la pantalla tampoco puede. El "si" es deliberado.
  "If {email} has an account, a link to choose a new password is on its way. It lasts {minutes} minutes and works once.":
    "Si {email} tiene cuenta, va de camino un enlace para elegir contraseña nueva. Dura {minutes} minutos y solo sirve una vez.",
  "Back to sign in": "Volver a entrar",
  "Choose a new password": "Elige una contraseña nueva",
  "Password again": "Repite la contraseña",
  "See the card": "Ver la carta",

  // Una tienda como punto de quedada.
  // THE STORE APPLICATION, on its own screen since the week of opening hours
  // stopped fitting in a dialog. "Tienda" throughout: the word on the door, and
  // the word the owner uses.
  "Open a store account": "Abrir una cuenta de tienda",
  // No "lo revisa una persona": the owner cut it. How we check is our business
  // and saying it here reads as a warning about a wait rather than a welcome.
  "For card shops with a counter. Free, usually within two working days.":
    "Para tiendas de cartas con mostrador. Gratis, normalmente en dos días laborables.",
  "The store": "La tienda",
  "Where and when": "Dónde y cuándo",
  "Check it over": "Repasa",
  "Give the name on the sign.": "Pon el nombre que hay en el cartel.",
  "As it reads on the sign. Traders look for it in the street.":
    "Tal y como está en el cartel. La gente lo busca por la calle.",
  // The page address the name will get, shown before the shop saves (2026-10-19).
  "Your store’s page": "La página de tu tienda",
  "Appears as you type the name.": "Aparece al escribir el nombre.",
  "Another store took that address a moment before you. Your store’s page is {address}.":
    "Otra tienda cogió esa dirección justo antes que tú. La página de tu tienda es {address}.",
  "Your store’s page is {address}.": "La página de tu tienda es {address}.",
  "What you sell": "Qué vendes",
  "One line about the store": "Una línea sobre la tienda",
  "Singles wall, sealed product and four tables.": "Pared de sueltas, producto sellado y cuatro mesas.",
  "Shown under the name on your page.": "Se ve bajo el nombre en tu página.",
  "Where the door is": "Dónde está la puerta",
  "Opening hours": "Horario",
  "Closed": "Cerrado",
  "closed": "cerrado",
  "Open on {day}": "Abierto el {day}",
  "Copy Monday to the rest of the week": "Copiar el lunes al resto de la semana",
  "Open at least one day.": "Abre al menos un día.",
  "One day closes before it opens.": "Un día cierra antes de abrir.",
  "Traders can meet at your counter": "Se puede quedar en tu mostrador",
  "You show up as a place to meet on trades nearby.": "Apareces como sitio para quedar en los cambios de cerca.",
  "A person checks the store exists before it goes live.": "Una persona comprueba que la tienda existe antes de publicarla.",
  "Give the street address of the store.": "Pon la dirección de la tienda.",
  "That phone needs its country code, like +34.": "A ese teléfono le falta el prefijo del país, como +34.",
  "Hours": "Horario",
  "Meeting people": "Quedadas",
  "No meeting at the counter": "No se queda en el mostrador",
  "Contact": "Contacto",
  "How traders will see it": "Cómo lo verá la gente",
  "Traders can meet here": "Se puede quedar aquí",
  "Your store is being checked": "Estamos revisando tu tienda",
  "{name} is live": "{name} ya está publicada",
  "Traders nearby can see it now, and can be offered it as a place to meet.":
    "Ya la ve la gente de cerca, y se puede ofrecer como sitio para quedar.",
  "See your page": "Ver tu página",
  "Changing the name or the address sends it back to be checked.":
    "Cambiar el nombre o la dirección la devuelve a revisión.",
  "Monday": "Lunes",
  "Tuesday": "Martes",
  "Wednesday": "Miércoles",
  "Thursday": "Jueves",
  "Friday": "Viernes",
  "Saturday": "Sábado",
  "Sunday": "Domingo",
  "You can meet here": "Se puede quedar aquí",
  "Pick at least one game, or all of them.": "Elige al menos un juego, o todos.",
  "Do you run a card shop?": "¿Tienes una tienda de cartas?",

  // --- EL MOSTRADOR: la tienda como cuenta propia -------------------------
  // "Tienda" y "mostrador" son las palabras del dueño. Nada de "panel",
  // "dashboard" ni "backoffice": quien abre esto está detrás de un mostrador,
  // no delante de un cuadro de mandos.
  "Shop account": "Cuenta de tienda",

  // --- EL MOSTRADOR: la trastienda ----------------------------------------
  // Vocabulario de tienda, no de panel de control. "Stock" se queda en inglés
  // a propósito: es la palabra que usan las tiendas de cartas en España.
  "The counter": "El mostrador",
  "Behind the counter": "En el mostrador",
  "Trading": "Mi cuenta",
  "Games shown": "Juegos mostrados",
  "Showing": "Mostrando",
  "Price of {card}": "Precio de {card}",
  "Change {count} prices": "Cambiar {count} precios",
  "Prices never go below one cent. A card that follows Cardmarket keeps its own price.":
    "Ningún precio baja de un céntimo. Una carta que sigue a Cardmarket conserva el suyo.",
  "Change them": "Cambiarlos",
  "Adjust by a percentage": "Ajustar un porcentaje",
  "Set one price for all": "Poner el mismo precio a todas",
  "Or type a percentage": "O escribe un porcentaje",
  "Negative to lower, positive to raise.": "En negativo baja, en positivo sube.",
  "New price": "Precio nuevo",
  "{n} live. Prices save when you leave the box.":
    "{n} publicadas. Los precios se guardan al salir de la casilla.",
  // Las dos fichas de «Mostrar» del stock: todas las cartas, o solo las sincronizadas.
  "All": "Todas",
  "Show": "Mostrar",
  // El botón del móvil que abre el cajón de órdenes; el nombre accesible dice el orden puesto.
  "Sort": "Ordenar",
  "Sort: {order}": "Ordenar: {order}",
  "Find a card": "Busca una carta",
  "We could not load your stock": "No hemos podido cargar tu stock",
  "Something went wrong at our end. It is still there.":
    "Algo ha fallado por nuestra parte. Sigue ahí.",
  // La casilla de cabecera marca todo lo que hay en los juegos mostrados,
  // páginas sin ver incluidas, no solo las filas en pantalla.
  "Select everything in the games shown": "Seleccionar todo en los juegos mostrados",
  "Taking down works on the rows on screen. Narrow the list, or take them down a page at a time.":
    "Quitarlos actúa solo sobre las filas en pantalla. Acota la lista o quítalos página a página.",
  "Cond.": "Estado",
  "Qty": "Uds.",
  // La columna que sustituye a las visitas: cuánta gente la tiene en su lista.
  "Looking": "La buscan",
  "Listed": "Publicada",
  "Trade": "Cambio",
  "today": "hoy",
  "{n} d": "{n} d",
  "Nothing matches": "No hay nada que encaje",
  "No stock yet": "Todavía no hay stock",
  "Nothing here fits that.": "Nada de aquí encaja con eso.",
  "Post a card or import a list. It shows on the map straight away.":
    "Publica una carta o importa una lista. Sale en el mapa al momento.",
  "Clear the search": "Limpiar la búsqueda",
  "Bulk actions": "Acciones en grupo",
  "{n} selected": "{n} seleccionadas",
  "1 selected": "1 seleccionada",
  "Change the price": "Cambiar el precio",
  // "Follow Cardmarket" was the shop bar's button and is gone: that button now
  // reads "Follow" with their wordmark beside it, so the name is the logo.
  // "Follow Cardmarket's price" below is a different control and stays.
  "They come off the market straight away. The cards stay in your stock.":
    "Salen del mercado al momento. Las cartas se quedan en tu stock.",
  "Keep them": "Dejarlas",
  "Accepted.": "Aceptado.",
  "Declined.": "Rechazado.",
  "Accepting holds the card for them.": "Al aceptar, la carta queda reservada.",
  "Agreed": "Acordado",
  "No time yet": "Sin hora",
  // Un cambio en el mostrador no tiene hora y no le falta: su hora es el
  // horario de la tienda.
  "While the shop is open": "En el horario de la tienda",
  "While you are open": "En tu horario",
  "Open this trade": "Abrir este cambio",
  // LA FILA DE UNA PETICIÓN: qué se acepta, antes de aceptarlo.
  "Buys at {price} · full price": "Compra a {price} · precio completo",
  "Buys at {price}": "Compra a {price}",
  "Offers {cash} for {listed} listed": "Ofrece {cash} por los {listed} que pides",
  "Offers {cash}": "Ofrece {cash}",
  "{n}% under": "{n} % menos",
  "Trades 1 card": "Ofrece 1 carta a cambio",
  "Trades {n} cards": "Ofrece {n} cartas a cambio",
  "Trades 1 card + {cash}": "Ofrece 1 carta + {cash}",
  "Trades {n} cards + {cash}": "Ofrece {n} cartas + {cash}",
  "Trades 1 card, you add {cash}": "Ofrece 1 carta y tú pones {cash}",
  "Trades {n} cards, you add {cash}": "Ofrece {n} cartas y tú pones {cash}",
  "Offered": "Cartas ofrecidas",
  "Came in {when}": "Recibida: {when}",
  "Nothing waiting on you": "No tienes nada pendiente",
  "Nothing agreed yet": "Todavía no hay nada acordado",
  "A request from a trader nearby shows up here first.":
    "Las peticiones de gente de cerca salen aquí primero.",
  "A trade you mark done waits here until they mark it done too.":
    "Lo que marcas como hecho espera aquí a que la otra parte también lo marque.",
  "Sent by you": "Enviados por ti",
  "This trade": "Este cambio",
  "Open in Requests": "Abrir en Peticiones",
  "Show older trades": "Ver cambios más antiguos",
  "Not in the list you are looking at — another game, or older. Shown because it was asked for.":
    "No está en la lista que estás viendo: es de otro juego o más antiguo. Lo mostramos porque lo has abierto tú.",
  "This trade is not one of yours": "Este cambio no es tuyo",
  "The link may be mistyped, or the trade belongs to another account.":
    "Puede que el enlace esté mal escrito, o que el cambio sea de otra cuenta.",
  "Open a trade from the list to see all of it here.": "Abre un cambio de la lista para verlo entero aquí.",
  "Nothing sent": "No has enviado nada",
  "A trade you propose waits here until they answer.": "Lo que propones espera aquí a que te respondan.",
  "Nothing finished yet": "Todavía no hay nada terminado",
  "Completed, declined and cancelled trades end up here.":
    "Aquí acaban los cambios completados, rechazados y cancelados.",
  "What traders see when they find you.": "Lo que ve la gente cuando te encuentra.",
  "{n} prices changed.": "{n} precios cambiados.",
  "{n} prices changed. {skipped} follow Cardmarket, so their prices stay as they are.":
    "{n} precios cambiados. {skipped} siguen a Cardmarket, así que mantienen el suyo.",
  // EL MOSTRADOR COMO SITIO DE QUEDADA. Un cambio con una tienda se hace en su
  // mostrador y dentro de su horario: el servidor lo exige, así que la pantalla
  // lo dice antes de que nadie escriba una cafetería.
  "Trades with a store happen at its counter.": "Los cambios con una tienda se hacen en su mostrador.",
  "The store is closed then.": "La tienda está cerrada a esa hora.",
  "Open {from} to {to} that day.": "Ese día abre de {from} a {to}.",
  "Withdraw the application": "Retirar la solicitud",
  "Withdrawing…": "Retirando…",
  "The application is withdrawn.": "Solicitud retirada.",
  "Switch to trading": "Volver a mi cuenta",
  "Your store needs a fix": "Tu tienda necesita un arreglo",
  "Work as {name}": "Trabajar como {name}",
  "You are signed in as yourself. Work as the shop to see its stock and its requests.":
    "Has entrado con tu cuenta. Entra como la tienda para ver su stock y sus peticiones.",
  "Your shop": "Tu tienda",
  "You have no shop on this account yet.": "Todavía no tienes ninguna tienda en esta cuenta.",
  "Today": "Hoy",
  "Waiting on you": "Pendiente de ti",
  "All requests": "Todas las peticiones",
  "Wants to trade": "Quiere cambiar",
  "Waiting to be completed": "Pendiente de cerrar",
  "Nothing is waiting on you. New requests show up here first.":
    "No tienes nada pendiente. Las peticiones nuevas salen aquí primero.",
  // "Buscan" y no "quieren": lo que dice el dato es que esa carta está en la
  // lista de alguien, que es gente que vendría a por ella.
  "People looking for your cards": "Gente que busca tus cartas",
  "Most looked for, in your stock": "Lo más buscado de tu stock",
  "Your stock": "Tu stock",
  "Nobody nearby is looking for anything you have listed yet.":
    "Todavía no hay nadie cerca buscando nada de lo que tienes publicado.",
  "person": "persona",
  "people": "personas",
  "A small price change usually moves them. Select them on your stock and adjust in one go.":
    "Un cambio pequeño de precio suele moverlas. Selecciónalas en tu stock y ajústalas de una vez.",
  "Stock": "Stock",
  "Requests": "Peticiones",
  "You can list stock and answer requests now. The badge and the map pin come with the check.":
    "Ya puedes publicar stock y responder peticiones. El sello y el punto en el mapa llegan con la revisión.",
  "A game store": "Una tienda",
  "Run a game store? Become a meetup point: people agreeing a swap nearby can choose your counter.":
    "¿Tienes una tienda? Sé punto de quedada: quien cierre un cambio cerca puede elegir tu mostrador.",
  "Apply as a store": "Apuntar mi tienda",
  "Your store": "Tu tienda",
  "Store name": "Nombre de la tienda",
  "Address": "Dirección",
  "What somebody reads to find the door.": "Lo que alguien lee para encontrar la puerta.",
  "Phone": "Teléfono",
  "Website": "Web",
  // Dónde más se puede llegar a una tienda. "WhatsApp" y "Telegram" no se
  // traducen: son nombres propios.
  "With the country code, like the phone.": "Con el prefijo del país, como el teléfono.",
  "Show WhatsApp on your page": "Enseñar el WhatsApp en tu página",
  "Telegram": "Telegram",
  "Show Telegram on your page": "Enseñar el Telegram en tu página",
  "Cardmarket username": "Usuario de Cardmarket",
  "The name your Cardmarket shop trades under, if you have one.":
    "El nombre con el que vendes en Cardmarket, si tienes.",
  "We check the address before it appears to anybody. Changing it later sends it back to be checked.":
    "Comprobamos la dirección antes de que la vea nadie. Si la cambias, vuelve a comprobarse.",
  "The pin is where you are set now. Stand in the store, or move your point first.":
    "El punto es donde estás ahora. Ponte en la tienda, o mueve tu punto antes.",
  "Set where you are first — that is where the store goes on the map.":
    "Pon antes dónde estás — ahí es donde va la tienda en el mapa.",
  "Sent. We check the address and tell you.": "Enviado. Comprobamos la dirección y te avisamos.",
  "Waiting to be checked": "Esperando a que lo comprobemos",
  // WHAT THEY ARE WAITING FOR, not how we do it. This said that a person looks
  // at every store by hand and that nothing on the site says you are one yet —
  // our process and a small refusal, in the sentence somebody reads while
  // waiting. The heading above it already says the application is being looked
  // at; what a shop wants from this line is how long.
  "It usually takes less than 48 hours.": "Normalmente no pasa de 48 horas.",
  "We could not confirm this": "No hemos podido confirmarlo",
  "Fix it and send it again.": "Corrígelo y vuelve a enviarlo.",
  // "SI LO ADMITE", because the counter is a switch the shop owns and this
  // sentence was promising it unconditionally. Reported by the owner reading it
  // on pre: a shop that has turned meetings off would have been told, by us,
  // that it can be offered as a place to meet.
  "A checked store trades under its own name, shows its address, and can be offered as a place to meet, if it allows it.":
    "Una tienda comprobada cambia con su propio nombre, enseña su dirección y, si lo admite, puede ofrecerse como sitio para quedar.",
  "Changing the name or address sends the store back to be checked. The badge says somebody confirmed this address.":
    "Cambiar el nombre o la dirección devuelve la tienda a comprobación. El sello dice que alguien confirmó esta dirección.",
  "Traders agreeing a swap nearby can choose it as the place to meet.":
    "Quien cierre un cambio cerca puede elegirla como sitio para quedar.",
  "We check the address against the pin before it appears to anybody. This usually takes a day.":
    "Comprobamos la dirección con el punto antes de que la vea nadie. Suele tardar un día.",
  "We could not confirm it. This is what we wrote:":
    "No hemos podido confirmarla. Esto es lo que escribimos:",
  "Fix it and send it again — it goes back in the queue.":
    "Corrígelo y vuelve a enviarlo — entra otra vez en la cola.",
  "Fix it and send again": "Corregir y reenviar",
  "These two do not match.": "Las dos no coinciden.",
  "New password": "Contraseña nueva",
  "Change my password": "Cambiar la contraseña",
  "That is too short. Use at least {min} characters.":
    "Es demasiado corta. Usa al menos {min} caracteres.",
  "Password changed": "Contraseña cambiada",
  "Everything that was signed in to this account has been signed out, including anything that was not you. Sign in with the new password.":
    "Se ha cerrado la sesión en todo lo que estuviera dentro de esta cuenta, incluido lo que no fueras tú. Entra con la contraseña nueva.",
  "Password links last an hour and work once. Ask for a fresh one and use the newest email.":
    "Los enlaces de contraseña duran una hora y sirven una vez. Pide otro y usa el correo más reciente.",
  "No file arrived. Choose it and try again.": "No ha llegado ningún archivo. Elígelo y vuelve a probar.",
  "Say where you trade from before importing — every listing needs it.":
    "Di desde dónde cambias antes de importar — todo anuncio lo necesita.",

  // Why one row did not become a listing. Read out of REASON_NAMES in
  // import.jsx, so every entry there needs one here — which is checked.
  "The row carries no Cardmarket article number": "La fila no lleva número de artículo de Cardmarket",
  "The Cardmarket article number is not a number": "El número de artículo de Cardmarket no es un número",
  "The row carries no Cardmarket product number": "La fila no lleva número de producto de Cardmarket",
  "The Cardmarket product number is not a number": "El número de producto de Cardmarket no es un número",
  "This card is not in our catalogue": "Esta carta no está en nuestro catálogo",
  "Not a physical card: sealed product or an online code": "No es una carta física: producto sellado o un código online",
  "A game this market does not trade in": "Un juego con el que aquí no se cambia",
  "The row carries no language": "La fila no lleva idioma",
  "A language we do not recognise": "Un idioma que no reconocemos",
  "A language that could be more than one": "Un idioma que puede ser más de uno",
  "The row carries no condition": "La fila no lleva estado",
  "A condition we do not recognise": "Un estado que no reconocemos",
  "The row carries no price": "La fila no lleva precio",
  "A price of zero": "Un precio de cero",
  "A price we could not read": "Un precio que no hemos podido leer",
  "An amount we could not read": "Una cantidad que no hemos podido leer",
  "More copies than one listing can hold": "Más copias de las que cabe en un anuncio",
  // "Ya anunciada", not "ya importada": the withdrawn ones were imported too and
  // they come back, so what actually distinguishes this group is that the advert
  // is up. Naming the wrong half would send somebody looking for a withdrawal
  // to undo that they have already undone.
  "Already listed, and left exactly as it was": "Ya anunciada, y tal cual estaba",
  "Already traded, and that record stays as it is": "Ya intercambiada, y ese registro se queda como está",
  "Already on your wants list, and left as it is": "Ya está en tu lista de wants, y se queda como está",
  "The same card appears twice in the file": "La misma carta aparece dos veces en el archivo",
  "Take a photo": "Hacer una foto",
  "None of these": "Ninguna de estas",
  // Under the notifications switch. The switch reads the browser; these read the
  // server, which is the half that can actually reach a phone.
  "No device is registered to receive notifications.": "Ningún dispositivo está registrado para recibir avisos.",
  "Device removed.": "Dispositivo eliminado.",
  "That device could not be removed.": "No se ha podido eliminar ese dispositivo.",
  /*
   * The invitation shown before Android's own prompt. Its job is to make the
   * system dialog answerable — on Android 13 a refusal is close to permanent —
   * so the words have to say what arrives, not that "we would like to send you
   * notifications", which says nothing anybody can weigh.
   */
  "Shall we tell you when something happens?": "¿Te avisamos cuando pase algo?",
  "Only these three, and only about you. You can change any of them later.": "Solo estas tres cosas, y solo sobre ti. Puedes cambiarlas cuando quieras.",
  "Somebody writes to you about a card": "Alguien te escribe por una carta",
  "Somebody is waiting on your answer to a trade": "Alguien espera tu respuesta a un intercambio",
  "A card on your wants list turns up near you": "Aparece cerca de ti una carta de tu lista de búsqueda",
  "Turn notifications on": "Activar los avisos",
  "Not now": "Ahora no",
  "Notifications are on for this device.": "Los avisos están activados en este dispositivo.",
  "Android did not allow notifications. You can turn them on in Settings.": "Android no ha permitido los avisos. Puedes activarlos en Ajustes.",
  /*
   * The two push probes. Plain about what each one proves, because a phone that
   * stays quiet after a test is the moment somebody decides where to look — and
   * "no funciona" sends them to the wrong place.
   */
  "Show a notification now": "Mostrar un aviso ahora",
  "Send a test notification": "Enviar un aviso de prueba",
  "If you can read this, this device can show our notifications.": "Si puedes leer esto, este dispositivo puede mostrar nuestros avisos.",
  "Shown. If nothing appeared, this device is blocking our notifications — check Cardboardmarket in Android's app settings, not here.": "Mostrado. Si no ha aparecido nada, este dispositivo está bloqueando nuestros avisos: revisa Cardboardmarket en los ajustes de aplicaciones de Android, no aquí.",
  "This device refused to show it ({detail}). Inside the Android app the permission belongs to the app: turn notifications on for Cardboardmarket in Android's app settings.": "Este dispositivo se ha negado a mostrarlo ({detail}). Dentro de la aplicación de Android el permiso pertenece a la aplicación: activa los avisos de Cardboardmarket en los ajustes de aplicaciones de Android.",
  "It could not be sent: {detail}": "No se ha podido enviar: {detail}",
  "{device}: accepted by the push service. If nothing arrived, it was dropped after that.": "{device}: aceptado por el servicio de avisos. Si no ha llegado nada, se ha perdido después de eso.",
  "{device}: this device's subscription had expired, so it has been removed. Turn the switch on again to make a new one.": "{device}: la suscripción de este dispositivo había caducado, así que se ha eliminado. Vuelve a activar el interruptor para crear una nueva.",
  "{device}: it failed — {detail}": "{device}: ha fallado — {detail}",
  "Background worker: {version}": "Proceso en segundo plano: {version}",
  "{device} · last reached {when}": "{device} · último aviso entregado el {when}",
  "{device} · never reached yet": "{device} · todavía no ha recibido ningún aviso",
  "This device": "Este dispositivo",
  "Ordered by how much each one looks like your photo. Pick the one you are holding.":
    "Ordenadas por lo que se parecen a tu foto. Elige la que tienes en la mano.",

  // --- inviting, and the draws ---------------------------------------------
  // "Sorteo" for a draw, "entrada" for an entry: both are what a Spanish player
  // says about a prize draw, and "rifa" is not — a rifa is sold by the ticket.
  "Invite friends": "Invita a tus amigos",
  "Your link": "Tu enlace",
  "Copy": "Copiar",
  "Or say your code": "O di tu código",
  "Share link": "Compartir enlace",
  "Copy code": "Copiar el código",
  "Code copied.": "Código copiado.",
  "Link copied. Paste it wherever your friends are.": "Enlace copiado. Pégalo donde estén tus amigos.",
  "We could not copy it. You can select it and copy it yourself.": "No hemos podido copiarlo. Puedes seleccionarlo y copiarlo tú.",
  "Trade cards with me on Cardboardmarket": "Cambia cartas conmigo en Cardboardmarket",
  "We could not load your invite link": "No hemos podido cargar tu enlace de invitación",
  "Your friends": "Tus amigos",
  "Nobody yet. They appear here as soon as somebody joins with your code.":
    "Todavía nadie. Aparecerán aquí en cuanto alguien se registre con tu código.",
  "Counts": "Cuenta",
  "Waiting": "Pendiente",
  // The product's own name, so it is the same word in both languages.
  "Cardboardmarket": "Cardboardmarket",
  "They count once they have done what the draw asks. We do not tell them you are waiting.":
    "Cuentan en cuanto hagan lo que pide el sorteo. No les decimos que estás esperando.",
  "Joined {day}": "Se registró el {day}",
  "No draw running": "Ningún sorteo en marcha",
  "No draw is running. Invite somebody anyway — friends who joined in the last 30 days count for the next one.":
    "No hay ningún sorteo en marcha. Invita igualmente: los amigos que se hayan registrado en los últimos 30 días cuentan para el siguiente.",
  "A draw usually counts friends from the 30 days before it opened, so inviting now is not wasted.":
    "Un sorteo suele contar a los amigos de los 30 días anteriores a su apertura, así que invitar ahora no se pierde.",
  "The next draw opens {day}, and counts friends from the {n} days before that. Inviting now counts.":
    "El siguiente sorteo abre el {day} y cuenta a los amigos de los {n} días anteriores. Invitar ahora cuenta.",
  "The next draw opens {day}. Friends who join once it is open count towards it.":
    "El siguiente sorteo abre el {day}. Cuentan los amigos que se registren cuando ya esté abierto.",
  "Checking…": "Comprobando…",
  "The next one opens {day}.": "El siguiente abre el {day}.",
  "Past draws": "Sorteos anteriores",

  // What a friend has to do. These are fragments that land inside a sentence,
  // so they are infinitives — "consigues una entrada cuando publiquen una carta".
  "post a card": "publiquen una carta",
  "post a want": "publiquen una búsqueda",
  "post a card or a want": "publiquen una carta o una búsqueda",
  "complete a trade": "completen un intercambio",
  "join": "se registren",

  "Every friend who joins and does this is one entry in the draw: {what}.":
    "Cada amigo que se registre y haga esto es una entrada en el sorteo: {what}.",
  "One entry for every friend who joins and does this: {what}.":
    "Una entrada por cada amigo que se registre y haga esto: {what}.",
  "{n} winners, drawn {day}.": "{n} ganadores, sorteados el {day}.",
  "your entries": "tus entradas",
  "{n} entries": "{n} entradas",
  "{n} entries so far": "{n} entradas hasta ahora",
  "{n} days left": "Quedan {n} días",
  "Starts {day}": "Empieza el {day}",
  "Open": "Abierto",
  "Ended": "Terminado",
  "Opens": "Abre",
  "Hide": "Ocultar",
  "See how": "Ver cómo",
  "Invite a friend": "Invita a un amigo",
  "Closes today.": "Termina hoy.",

  // The draw's own page.
  "This draw is not here": "Este sorteo no está aquí",
  "It may have finished a while ago, or the link may be wrong.":
    "Puede que terminara hace tiempo, o que el enlace no sea correcto.",
  "Browse cards": "Explorar cartas",
  "The prize": "El premio",
  "Your entries": "Tus entradas",
  "Entries so far": "Entradas hasta ahora",
  "Winners": "Ganadores",
  "Join to take part": "Regístrate para participar",
  "take part in the draw": "participar en el sorteo",
  "Copy your link": "Copiar tu enlace",
  "How the draw works": "Cómo funciona el sorteo",
  "Waiting for their draw": "Esperando su sorteo",
  "Which one would you like?": "¿Cuál prefieres?",
  "Your choice": "Tu elección",
  "What you chose": "Lo que elegiste",
  "You can change your mind later.": "Puedes cambiar de idea más tarde.",
  "You can change this until we hand it over.": "Puedes cambiarlo hasta que te lo entreguemos.",
  "We could not save that. Try again in a moment.": "No hemos podido guardarlo. Inténtalo dentro de un momento.",
  "One entry for each friend who joined between {from} and {to}.":
    "Una entrada por cada amigo que se registrara entre el {from} y el {to}.",

  // --- finding out whether you won ---------------------------------------
  "The draw is done": "El sorteo ya está hecho",
  "A draw I entered has been drawn": "Se ha sorteado un sorteo en el que participo",
  "The winners are out — it never says whether you won":
    "Ya hay ganadores: nunca te dice si has ganado tú",
  "A new draw opens": "Empieza un sorteo nuevo",
  "We are running a draw and you can enter by inviting somebody":
    "Tenemos un sorteo en marcha y puedes participar invitando a alguien",
  "Not drawn": "Sin premio",
  "Check if I won": "Ver si he ganado",
  "Entry {n}": "Entrada {n}",
  /*
   * Lower case on purpose: these sit on a card in display type, like the
   * headlines below them.
   *
   * AND SHORT, because the card is 128px wide and 92px on a phone, and these
   * are set at 26px with no room to wrap — "premiada" ran straight off the
   * edge, reported with a screenshot. "ganó" and "no ganó" say the same thing
   * about the entry in four letters and wrap cleanly when they have to.
   */
  "drawn": "ganó",
  "not drawn": "no ganó",
  "Shuffling {n} entries": "Barajando {n} entradas",
  "Checking your {n} entries": "Comprobando tus {n} entradas",
  "Checking your 1 entry": "Comprobando tu entrada",
  "1 of the {total} entries was yours. {winners} winners to draw.":
    "1 de las {total} entradas era tuya. Se sortean {winners} ganadores.",
  "drawn {day}": "sorteado el {day}",
  "you won a {prize}": "has ganado: {prize}",
  "One of {n} drawn from {total} entries.": "Una de las {n} premiadas entre {total} entradas.",
  "not this time": "esta vez no",
  "{winners} winners were drawn from {total} entries, and yours were not among them. Thanks for bringing people in — they are still here trading.":
    "Se sortearon {winners} ganadores entre {total} entradas, y las tuyas no estaban. Gracias por traer gente: siguen aquí cambiando cartas.",
  "Every friend who joins and does this adds an entry: {what}.":
    "Cada amigo que se registre y haga esto suma una entrada: {what}.",
  "Your link keeps working — friends who join once it opens count.":
    "Tu enlace sigue funcionando: los amigos que se registren cuando abra contarán.",
  "See the draw": "Ver el sorteo",
  "{n} of the {total} entries were yours. {winners} winners to draw.":
    "{n} de las {total} entradas eran tuyas. Se sortean {winners} ganadores.",
  "The prize is collected in person, with the code above. No cash alternative.":
    "El premio se recoge en persona, con el código de arriba. No hay alternativa en metálico.",
  "We message the winner for a postal address and send the prize. It is the only thing we ever post.":
    "Escribimos al ganador para pedirle una dirección postal y le enviamos el premio. Es lo único que enviamos por correo.",
  "{n} winners are drawn on {day}.": "Se sortean {n} ganadores el {day}.",
  "You pick one of these": "Eliges uno de estos",
  "You won {prize}.": "Has ganado: {prize}.",
  "You collected this one.": "Ya lo has recogido.",
  "Pickup code": "Código de recogida",
  "Show this code when you collect it.": "Enseña este código al recogerlo.",
  "We will message you to arrange it.": "Te escribiremos para organizarlo.",
  "You won": "Has ganado",
  "A friend counts once they join with your link or code and {what}.":
    "Un amigo cuenta cuando se registra con tu enlace o tu código y {what}.",
  "A friend counts once they join with your link or code and {what} within {n} days.":
    "Un amigo cuenta cuando se registra con tu enlace o tu código y {what} en los {n} días siguientes.",
  "Up to {n} entries per person.": "Hasta {n} entradas por persona.",
  "Joining counts on its own: sign up, {what}, and you are in the draw.":
    "Entrar cuenta por sí solo: regístrate, {what}, y ya estás en el sorteo.",
  "Your friend gets one entry too.": "Tu amigo consigue una entrada también.",
  "Only new accounts count, one per person.": "Solo cuentan las cuentas nuevas, una por persona.",

  // Arriving by somebody's link.
  "{name} invited you to trade": "{name} te invita a cambiar cartas",
  "Cardboardmarket matches you with card holders a few streets away. You meet somewhere public, check the card and hand it over. No posting.":
    "Cardboardmarket te pone en contacto con gente que tiene cartas a unas calles de aquí. Quedáis en un sitio público, miráis la carta y os la dais en mano. Sin envíos.",
  "When you {what}": "Cuando {what}",
  "1 entry": "1 entrada",
  "in the draw for {prize}": "en el sorteo de {prize}",
  "Join with {name}\u2019s code": "Regístrate con el código de {name}",
  "Join Cardboardmarket": "Regístrate en Cardboardmarket",
  "Takes a minute. Next you set a point and a radius, and see what is listed around you.":
    "Se tarda un minuto. Después marcas un punto y un radio, y ves lo que hay publicado a tu alrededor.",
  "Applied": "Aplicado",
  "Not a code we know": "No conocemos este código",
  "Join": "Registrarme",
  "Look around first": "Primero echo un vistazo",

  // An operator correcting a listing's printing, from the listing page.
  "Fix this listing": "Corregir este anuncio",
  "Choose the printing the seller actually has. The title and the set are taken from the catalogue; the price, the condition, the copies and the photos stay as the seller set them.":
    "Elige la edición que tiene de verdad el vendedor. El título y la colección salen del catálogo; el precio, el estado, las copias y las fotos se quedan como los dejó el vendedor.",
  "Now: {title} · {set} · #{id}": "Ahora: {title} · {set} · #{id}",
  "Printing": "Edición",
  // The printing tiles and the before/after frames. See printing-tiles.jsx.
  "Current": "Actual",
  "Correction": "Corrección",
  "Choose a card and a printing below.": "Elige abajo una carta y una edición.",
  "Save the correction": "Guardar la corrección",
  "Listing corrected. The seller has a message saying what changed.":
    "Anuncio corregido. El vendedor tiene un mensaje con lo que ha cambiado.",
  "This listing comes from the seller's CardTrader stock, so it is read-only here. The printing has to be corrected on CardTrader; the next sync brings it across.":
    "Este anuncio viene del stock de CardTrader del vendedor, así que aquí es de solo lectura. La edición se corrige en CardTrader y la próxima sincronización la trae.",
  "That is already the printing, language and finish this listing has.":
    "Este anuncio ya tiene esa edición, ese idioma y ese acabado.",
  "The catalogue does not hold that printing any more. Pick another.":
    "El catálogo ya no tiene esa edición. Elige otra.",
  "Somebody changed this listing a moment ago. Close this and open it again.":
    "Alguien acaba de cambiar este anuncio. Cierra esto y vuelve a abrirlo.",
  "This listing is no longer up, so there is nothing to correct.":
    "Este anuncio ya no está publicado, así que no hay nada que corregir.",
  "Only an operator can correct a listing.": "Solo un operador puede corregir un anuncio.",
  "That did not save. Try again in a moment.": "No se ha guardado. Vuelve a probar en un momento.",

  // --- asked before it is done (the owner, 2026-10-23) ------------------------
  // ConfirmDialog in chrome.jsx. The title says what and how many; the button
  // says the act, never "OK"; Cancel is the one focused.
  "Remove “{card}” from your wants?": "¿Quitar «{card}» de tus búsquedas?",
  "We stop watching for it. Adding it again means filling the form again.":
    "Dejamos de buscarla. Para volver a añadirla tendrás que rellenar el formulario otra vez.",
  "Remove want": "Quitar búsqueda",
  "Remove 1 want?": "¿Quitar 1 búsqueda?",
  "Remove {n} wants?": "¿Quitar {n} búsquedas?",
  "We stop watching for them. Adding them again means filling the form again.":
    "Dejamos de buscarlas. Para volver a añadirlas tendrás que rellenar el formulario otra vez.",
  "Remove 1 want": "Quitar 1 búsqueda",
  "Remove {n} wants": "Quitar {n} búsquedas",
  "Decline this trade?": "¿Rechazar este intercambio?",
  "Withdraw this proposal?": "¿Retirar esta propuesta?",
  "{name} is told, and this proposal cannot be reopened.":
    "Se lo diremos a {name}, y esta propuesta no se puede reabrir.",
  "The other side is told, and this proposal cannot be reopened.":
    "Se lo diremos a la otra parte, y esta propuesta no se puede reabrir.",
  "Decline trade": "Rechazar intercambio",
  "Withdraw proposal": "Retirar propuesta",
  "Remove this photo?": "¿Quitar esta foto?",
  "It is deleted, and traders see the listing without it. You can add another.":
    "Se borra, y los demás verán el anuncio sin ella. Puedes añadir otra.",
  "Remove photo": "Quitar foto",
  "Delete “{label}”?": "¿Eliminar «{label}»?",
  "Its point and its name are deleted. Setting it again means dropping the pin again.":
    "Se borran su punto y su nombre. Para recuperarla tendrás que volver a poner el pin.",
  "Delete location": "Eliminar ubicación",
  "Withdraw your application?": "¿Retirar tu solicitud?",
  "What you sent is deleted and nobody checks it. Applying again means filling the form again.":
    "Lo que enviaste se borra y nadie lo revisa. Para volver a solicitarlo tendrás que rellenar el formulario otra vez.",
  "Remove {device}?": "¿Quitar {device}?",
  "We stop sending it notifications. Switching them on again from that device brings it back.":
    "Dejamos de enviarle avisos. Si los vuelves a activar desde ese dispositivo, vuelve a la lista.",
  "Remove device": "Quitar dispositivo",
  // And "anywhere we're changing something kind of important" (the owner, same day).
  "Mark this trade done?": "¿Marcar este intercambio como hecho?",
  "Only once the cards have changed hands. It cannot be undone: when you have both marked it, the copies come off the listing.":
    "Solo cuando las cartas hayan cambiado de manos. No se puede deshacer: cuando lo hayáis marcado los dos, las copias salen del anuncio.",
  "Yes, it is done": "Sí, está hecho",
  "Send your store to be checked again?": "¿Enviar tu tienda a revisar otra vez?",
  "Changing the name, the address or the pin sends the store back to be checked. Until it is, it is no longer a verified store and its page is not shown.":
    "Cambiar el nombre, la dirección o el punto devuelve la tienda a revisión. Hasta entonces deja de ser una tienda verificada y su página no se muestra.",
  "Save and send to be checked": "Guardar y enviar a revisión",

  // On the candidate the pictures put clearly ahead of the rest. One word,
  // because it sits on a row that already carries a name and a set.
};

/**
 * English is the source language: keys ARE the English strings, so its dictionary
 * is empty by construction and every lookup falls through to the key itself.
 * That is why a missing key only counts as a miss in another locale.
 */
const FALLBACK_LOCALE = "en-US";
export const DICTIONARIES = { "es-ES": ES, "en-US": {} };

/** Spanish unless the browser explicitly asks for English. */
export function browserLocale() {
  try {
    return String(navigator.language || "").toLowerCase().startsWith("en") ? "en-US" : "es-ES";
  } catch (e) {
    return "es-ES";
  }
}

export const STORE_KEY = "cbm.locale";

/**
 * A remembered language choice.
 *
 * The one thing this app stores in the browser, and worth justifying: it is a
 * preference, not an identifier. It says nothing about who somebody is, cannot be
 * used to recognise them, and is the same value for everyone who picks Spanish. A
 * consent banner exists for tracking, not for remembering which language a page
 * should be in.
 */
export function readStored() {
  try {
    const v = window.localStorage.getItem(STORE_KEY);
    return DICTIONARIES[v] ? v : null;
  } catch (e) {
    return null;
  }
}

export function writeStored(locale) {
  try { window.localStorage.setItem(STORE_KEY, locale); } catch (e) { /* private mode */ }
}

/**
 * Every string the app asked for and did not get, per locale.
 *
 * Recorded as it happens rather than diffed against a list of keys, because the
 * only trustworthy inventory of what the app displays is what the app displays.
 * A key nobody wraps in t() is invisible to any static check; a key that IS
 * wrapped but has no Spanish renders its English source, which looks like a
 * finished screen to a screenshot and like a bug to a user in Murcia.
 */
const MISSED = {};

export function translate(locale, key, vars) {
  const dict = DICTIONARIES[locale] || {};
  // Falling back to the key means a missing translation shows a real sentence
  // rather than "browse.title" leaking onto the screen.
  let out = key;
  if (dict[key] !== undefined && dict[key] !== '') {
    out = dict[key];
  } else if (locale !== FALLBACK_LOCALE) {
    // en-US is the source language, so a key with no entry there is not a miss.
    (MISSED[locale] = MISSED[locale] || new Set()).add(key);
  }
  if (vars) for (const [k, v] of Object.entries(vars)) out = out.split(`{${k}}`).join(String(v));
  return out;
}

/** What the app has asked for in this locale and could not translate. Used by a test. */
export function missingTranslations(locale) {
  return [...(MISSED[locale] || [])].sort();
}

/*
 * The one thing that stays on `window`, and it is not for the app.
 *
 * `missingTranslations` exists so a Playwright test can ask the running page
 * what it was asked to translate and could not — see the two checks in
 * e2e/tests/language.spec.ts, which reach it through page.evaluate. A module
 * export is invisible to that: the bundle closes over its own scope and the
 * test has nothing to import from.
 *
 * So this is a TEST HOOK, deliberately, and it is now only the hook. The other
 * six names went with the conversion; store.jsx imports them.
 */
window.CbmI18n = { missingTranslations };
